import React, { useState, useEffect, useMemo } from 'react';
import { APP_NAME } from '@/lib/brand';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { usePlatformBaseUrl } from '@/lib/platformBaseUrl';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import FieldTooltip from '@/components/ui/FieldTooltip';
import { Building2, Users, Target, BarChart3, Plus, Trash2, ShieldCheck, Eye, ArrowUpRight, User, Search, Bot, Activity, Key, Loader2, CheckCircle2, ExternalLink, Save, DollarSign, ToggleLeft, ToggleRight, TrendingUp, MapPin, Globe, FileText, Shield, Clock, Pencil, Package, Send, Sparkles, Phone, HardDrive, PauseCircle } from 'lucide-react';
import PhoneInput from '@/components/PhoneInput';
import CopyableId from '@/components/CopyableId';
import IntegrationsBoard from './superadmin/IntegrationsBoard';
import PlanEditorDialog from './superadmin/PlanEditorDialog';
import InventoryPanel from './superadmin/InventoryPanel';
import ReportsPanel from './superadmin/ReportsPanel';
import { isPlatformOwner } from '@/lib/platformOwner';
import {
  BTYPE_LABELS, BTYPE_COLORS, INDUSTRY_OPTIONS, INDUSTRY_SUBTYPES, INDUSTRY_LABELS,
  PLAN_COLORS, LICENSE_TYPE_OPTIONS, getPriceMatrix,
  planMatchesNewAccount, isCatalogIndustry,
} from './superadmin/constants';
import { capLabel } from './superadmin/planCatalog';

function usd(n, digits = 4) {
  const v = Number(n || 0);
  if (!Number.isFinite(v) || v === 0) return '$0';
  if (Math.abs(v) < 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(digits)}`;
}

function AutoLoader({ tab, loaders }) {
  useEffect(() => {
    if (loaders[tab]) loaders[tab]();
  }, []); // eslint-disable-line
  return null;
}

export default function SuperAdminPage({ initialTab = 'companies' }) {
  const { impersonate, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', admin_name: '', admin_email: '', admin_password: '', phone: '', business_type: 'agency', plan: '', industry: '', industry_subtype: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [storageEditCompany, setStorageEditCompany] = useState(null);
  const [vozTenant, setVozTenant] = useState(null);
  const [vozForm, setVozForm] = useState({ phone_number: '', account_sid: '', auth_token: '' });
  const [vozTenantSaving, setVozTenantSaving] = useState(false);
  const saveVozTenant = async () => {
    if (!vozForm.phone_number || !vozForm.account_sid || !vozForm.auth_token) { toast.error('Completa numero, Account SID y Auth Token'); return; }
    setVozTenantSaving(true);
    try {
      await api.post('/admin/platform/twilio/associate', { company_id: vozTenant.id, phone_number: vozForm.phone_number, role: 'ia_voz', origen: 'tenant', account_sid: vozForm.account_sid, auth_token: vozForm.auth_token });
      toast.success(`Voz IA de ${vozTenant.name} configurada con su Twilio`);
      setVozTenant(null); setVozForm({ phone_number: '', account_sid: '', auth_token: '' });
    } catch (e) { toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || 'No se pudo guardar'); }
    finally { setVozTenantSaving(false); }
  };
  const [storageEditMb, setStorageEditMb] = useState('');
  const [storageSaving, setStorageSaving] = useState(false);
  const [biblioR2Saving, setBiblioR2Saving] = useState(false);
  const [aiTenantId, setAiTenantId] = useState('');
  const [aiTenants, setAiTenants] = useState([]);
  const [showInactivos, setShowInactivos] = useState(false);
  const [infraMxn, setInfraMxn] = useState(500000);
  const [infraSaving, setInfraSaving] = useState(false);
  const [usabilidad, setUsabilidad] = useState(null);
  const [vozIa, setVozIa] = useState(null);
  const [vozSaving, setVozSaving] = useState(false);
  const [aiDias, setAiDias] = useState(30);
  // Dominio oficial servido por el backend (FRONTEND_URL); "" mientras carga / si falta.
  const platformBase = usePlatformBaseUrl();
  const calendlyRedirectUri = platformBase ? `${platformBase}/api/auth/calendly/callback` : '';
  const mercadopagoWebhook = platformBase ? `${platformBase}/api/webhooks/mercadopago` : '';
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditCompanyId, setAuditCompanyId] = useState('');
  const [dynPlans, setDynPlans] = useState([]);
  const [platformIntegrations, setPlatformIntegrations] = useState(null);
  const [orWallet, setOrWallet] = useState(null);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [sendGridConfig, setSendGridConfig] = useState({ sendgrid_api_key: '', sendgrid_from_email: '', sendgrid_from_name: '', is_active: true, configured: false });
  const [sendGridTestEmail, setSendGridTestEmail] = useState('');
  const [sendGridBusy, setSendGridBusy] = useState(false);
  const [calendlyGuide, setCalendlyGuide] = useState(null);
  const [integrationDialog, setIntegrationDialog] = useState(null);
  const [testingInteg, setTestingInteg] = useState(null);
  const [integrationCreds, setIntegrationCreds] = useState({});
  const [dynPlansLoading, setDynPlansLoading] = useState(false);
  const [planDialog, setPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const loadAuditLogs = async (companyId) => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (companyId) params.append('company_id', companyId);
      const r = await api.get(`/admin/audit-logs?${params.toString()}`);
      setAuditLogs(r.data.logs || []);
      setAuditTotal(r.data.total || 0);
    } catch { /* ok */ }
    finally { setAuditLoading(false); }
  };

  const loadDynPlans = async () => {
    setDynPlansLoading(true);
    try { const r = await api.get('/admin/dynamic-plans'); setDynPlans(r.data || []); }
    catch { /* ok */ }
    finally { setDynPlansLoading(false); }
  };

  // Saldo de OpenRouter para la pestana Uso de IA. Si falla, la pantalla
  // sigue funcionando: solo deja de pintar el recuadro.
  const loadOpenRouter = () => {
    api.get('/admin/ai/openrouter')
      .then((r) => setOrWallet(r.data))
      .catch(() => setOrWallet({ connected: false, mensaje: 'No se pudo leer OpenRouter' }));
  };
  useEffect(loadOpenRouter, []);

  const loadPlatformIntegrations = async () => {
    setPlatformLoading(true);
    try { const r = await api.get('/admin/platform/integrations'); setPlatformIntegrations(r.data); }
    catch { /* ok */ }
    finally { setPlatformLoading(false); }
  };

  const openInteg = (key) => {
    const integ = platformIntegrations?.[key] || {};
    setIntegrationDialog(key);
    if (key === 'r2') {
      setIntegrationCreds({});
    } else if (key === 'gupshup') {
      const c = { ...(integ.credentials || {}) };
      delete c.partner_secret;
      delete c.partner_secret_2;
      setIntegrationCreds(c);
    } else {
      setIntegrationCreds(integ.credentials || {});
    }
    if (key === 'calendly' && !calendlyGuide) loadCalendlyGuide();
    if (key === 'sendgrid') loadSendGridConfig();
  };

  const testInteg = async (key) => {
    setTestingInteg(key);
    try {
      const r = await api.post(`/admin/platform/integrations/${key}/test`);
      if (r.data?.ok) toast.success(r.data.mensaje || 'Conexión lista');
      else toast.error(r.data?.mensaje || 'Falta configurar');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo probar');
    } finally { setTestingInteg(null); }
  };

  const savePlatformCredentials = async (key) => {
    try {
      const payload = { ...integrationCreds };
      if (key === 'ai_tokens') {
        // Vacio o enmascarado = no se toca la que ya estaba guardada.
        ['openrouter_api_key', 'openai_key', 'anthropic_key', 'google_key'].forEach((k) => {
          const v = String(payload[k] || '');
          if (!v.trim() || v.includes('*')) delete payload[k];
        });
      }
      if (key === 'gupshup') {
        const secret = String(payload.partner_secret || '');
        if (!secret.trim() || secret.includes('*')) delete payload.partner_secret;
        const secret2 = String(payload.partner_secret_2 || '');
        if (!secret2.trim() || secret2.includes('*')) delete payload.partner_secret_2;
      }
      await api.put(`/admin/platform/integrations/${key}`, { credentials: payload });
      toast.success(key === 'gupshup' ? 'Gupshup guardado. Ya puedes pedir aprobación de plantillas.' : 'Credenciales guardadas');
      setIntegrationDialog(null);
      setIntegrationCreds({});
      loadPlatformIntegrations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
  };

  const removePlatformIntegration = async (key) => {
    if (!window.confirm('Desconectar esta integracion?')) return;
    try {
      await api.delete(`/admin/platform/integrations/${key}`);
      toast.success('Integracion desconectada');
      loadPlatformIntegrations();
    } catch (e) { toast.error('Error'); }
  };

  const loadCalendlyGuide = async () => {
    try { const r = await api.get('/admin/platform/calendly-guide'); setCalendlyGuide(r.data); }
    catch { /* ok */ }
  };

  const loadSendGridConfig = async () => {
    try {
      const r = await api.get('/admin/platform/email-config');
      setSendGridConfig({
        sendgrid_api_key: '',
        sendgrid_from_email: r.data.sendgrid_from_email || '',
        sendgrid_from_name: r.data.sendgrid_from_name || '',
        is_active: r.data.is_active !== false,
        configured: !!r.data.configured,
        masked_key: r.data.sendgrid_api_key || '',
      });
    } catch { /* ok */ }
  };

  const saveSendGridConfig = async () => {
    if (!sendGridConfig.sendgrid_api_key) { toast.error('Ingresa la API Key de SendGrid'); return; }
    if (!sendGridConfig.sendgrid_from_email) { toast.error('Ingresa el email remitente'); return; }
    setSendGridBusy(true);
    try {
      // Si no había configuración previa, forzar is_active=true para que el provider
      // lo tome automáticamente (evita que se guarde y luego parezca no configurado).
      const forceActive = !sendGridConfig.configured ? true : sendGridConfig.is_active;
      await api.post('/admin/platform/email-config', {
        sendgrid_api_key: sendGridConfig.sendgrid_api_key,
        sendgrid_from_email: sendGridConfig.sendgrid_from_email,
        sendgrid_from_name: sendGridConfig.sendgrid_from_name,
        is_active: forceActive,
      });
      toast.success('SendGrid validado y guardado');
      loadSendGridConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSendGridBusy(false); }
  };

  const testSendGridConfig = async () => {
    if (!sendGridTestEmail) { toast.error('Ingresa un email destino'); return; }
    setSendGridBusy(true);
    try {
      await api.post('/admin/platform/email-config/test', { to_email: sendGridTestEmail });
      toast.success('Email de prueba enviado!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al enviar'); }
    finally { setSendGridBusy(false); }
  };

  const openPlanEditor = (plan) => {
    setEditingPlan(plan || null);
    setPlanDialog(true);
  };

  const deletePlan = async (plan) => {
    const seed = plan?.is_seed;
    const msg = seed
      ? `Archivar el plan de sistema "${plan?.name}"? Se puede recuperar; no se borra de fabrica.`
      : `Eliminar el plan "${plan?.name}"?`;
    if (!window.confirm(msg)) return;
    try { const r = await api.delete(`/admin/dynamic-plans/${plan.id}`); toast.success(r?.data?.archived ? 'Plan de sistema archivado' : 'Plan eliminado'); loadDynPlans(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const load = () => {
    Promise.all([
      api.get('/admin/stats'),
      api.get('/admin/dynamic-plans').catch(() => ({ data: [] })),
    ])
      .then(([r, dp]) => {
        setStats(r.data);
        setDynPlans(dp.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => {
    api.get('/admin/ai/tenants', { params: { include_inactive: showInactivos } })
      .then((r) => setAiTenants(r.data?.tenants || []))
      .catch(() => setAiTenants([]));
    api.get('/admin/ai/infra').then((r) => {
      if (r.data?.infra_mxn_mes != null) setInfraMxn(r.data.infra_mxn_mes);
    }).catch(() => {});
  }, [showInactivos]);
  useEffect(() => {
    if (!aiTenantId) { setUsabilidad(null); setVozIa(null); return; }
    let vivo = true;
    api.get(`/admin/ai/usabilidad/${aiTenantId}`, { params: { dias: aiDias } })
      .then((r) => { if (vivo) setUsabilidad(r.data); })
      .catch(() => { if (vivo) setUsabilidad(null); });
    api.get(`/admin/ai/voz/${aiTenantId}`)
      .then((r) => { if (vivo) setVozIa(r.data); })
      .catch(() => { if (vivo) setVozIa(null); });
    return () => { vivo = false; };
  }, [aiTenantId, aiDias]);

  const filteredCompanies = useMemo(() => {
    if (!stats?.companies) return [];
    if (!search.trim()) return stats.companies;
    const q = search.toLowerCase();
    return stats.companies.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.contact_email?.toLowerCase().includes(q) ||
      (c.business_type || '').includes(q)
    );
  }, [stats?.companies, search]);

  const selectedAiTenant = useMemo(() => {
    if (!aiTenantId) return null;
    const row = (aiTenants || []).find((e) => e.company_id === aiTenantId);
    return { nombre: row?.nombre || aiTenantId, activa: row?.activa !== false };
  }, [aiTenantId, aiTenants]);

  const availableDynamicPlans = useMemo(() => {
    const bt = form.business_type || 'agency';
    const industry = form.industry || '';
    const active = (dynPlans || []).filter((p) => p?.is_active);
    const matched = active.filter((p) => planMatchesNewAccount(p, { businessType: bt, industry }));
    const list = matched.length
      ? matched
      : active.filter((p) => {
          const vis = Array.isArray(p?.visible_for_types) ? p.visible_for_types : [];
          if (!vis.length) return true;
          return vis.includes(bt);
        });
    return list.sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));
  }, [dynPlans, form.business_type, form.industry]);

  useEffect(() => {
    if (!availableDynamicPlans.length) {
      if (form.plan) setForm(prev => ({ ...prev, plan: '' }));
      return;
    }
    if (!form.plan || !availableDynamicPlans.some(p => p.id === form.plan)) {
      setForm(prev => ({ ...prev, plan: availableDynamicPlans[0].id }));
    }
  }, [availableDynamicPlans]); // eslint-disable-line

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.plan) {
      toast.error('Selecciona un plan dinámico válido');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/companies', form);
      toast.success('Cuenta creada exitosamente');
      setShowCreate(false);
      setForm({ name: '', admin_name: '', admin_email: '', admin_password: '', phone: '', business_type: 'agency', plan: '', industry: '', industry_subtype: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear'); }
    finally { setSaving(false); }
  };

  const handleSuspend = async (id, name) => {
    if (!window.confirm(`¿Suspender "${name}"?\n\nSale de Cuentas, se apagan WhatsApp, recetas y llaves. El equipo no puede entrar. Puedes reactivarla en Suspensiones.`)) return;
    try {
      await api.post(`/admin/companies/${id}/suspend`, { motivo: 'Suspendida desde Cuentas' });
      toast.success('Cuenta suspendida. Está en Suspensiones.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo suspender');
    }
  };

  const handleImpersonate = async (companyId) => {
    try {
      await impersonate(companyId);
      toast.success('Suplantacion activada');
      navigate('/dashboard');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al suplantar'); }
  };

  // WS-IA-UNIFIED F3 — acceso directo a la config de IA de una compañía:
  // impersona y abre la ruta de IA correspondiente (sin pasos manuales).
  // Usamos window.location.assign (navegación completa) en vez de navigate()
  // porque /backoffice es superadmin-only: tras impersonar, el guard de la ruta
  // gana la carrera con navigate() y redirige a /dashboard. Una navegación dura
  // reconstruye el AuthContext con el token impersonado y aterriza en la ruta IA.
  const handleConfigureAI = async (companyId, route) => {
    try {
      await impersonate(companyId);
      toast.success('Abriendo configuración de IA de la compañía');
      window.location.assign(route);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al acceder a la configuración IA'); }
  };

  const handleUpgrade = async (companyId, name) => {
    if (!window.confirm(`Actualizar "${name}" a Inmobiliaria?`)) return;
    try { await api.post(`/admin/upgrade/${companyId}`); toast.success('Actualizado a Inmobiliaria'); load(); } catch { toast.error('Error'); }
  };

  const handleChangeLicense = async (companyId, name, currentType, newType) => {
    if (!newType || newType === currentType) return;
    const newLabel = BTYPE_LABELS[newType] || newType;
    const oldLabel = BTYPE_LABELS[currentType] || currentType;
    if (!window.confirm(`Cambiar licencia de "${name}":\n\n${oldLabel}  →  ${newLabel}\n\nEsto ajustara los modulos disponibles para la cuenta. ¿Continuar?`)) return;
    try {
      await api.patch(`/admin/companies/${companyId}/license`, { business_type: newType });
      toast.success(`Licencia de ${name} cambiada a ${newLabel}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar licencia');
    }
  };

  // Encender/apagar el módulo de seguimientos (Lógicas/cascada) de una empresa.
  // Superadmin-only (la página ya lo es). Confirm SOLO al apagar (cancela cascadas en curso).
  const handleChangeIndustry = async (companyId, name, currentInd, newInd) => {
    if (!newInd || newInd === currentInd) return;
    const io = INDUSTRY_OPTIONS.find(x => x.value === newInd);
    const newLabel = io ? `${io.icon} ${io.label}` : newInd;
    if (!window.confirm(`Cambiar GIRO de "${name}" a ${newLabel}.

El giro define que inventario lee la IA (propiedades/desarrollos/productos/autos) y los modulos de catalogo. Continuar?`)) return;
    try {
      await api.patch(`/admin/companies/${companyId}/industry`, { industry: newInd });
      toast.success(`Giro de ${name} cambiado a ${io?.label || newInd}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar giro');
    }
  };

  const handleToggleCascade = async (id, name, isOn) => {
    if (isOn && !window.confirm(`¿Apagar seguimientos de "${name}"?\n\nEsto cancelará los seguimientos en curso de esta empresa.`)) return;
    try {
      const r = await api.put(`/admin/companies/${id}/cascade-activation`, { enabled: !isOn });
      toast.success(isOn
        ? `Seguimientos apagados (${r.data?.cascades_cancelled ?? 0} en curso cancelados)`
        : `Seguimientos activados para ${name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar');
    }
  };

  // Límite de almacenamiento de Biblioteca por empresa (edición manual, superadmin-only,
  // TICKET_LIMITE_ALMACENAMIENTO.md — sin flujo de compra/pago).
  const openStorageEdit = (c) => {
    setStorageEditCompany(c);
    setStorageEditMb(String(Math.round((c.storage_limit_bytes || 1073741824) / (1024 * 1024))));
  };

  const saveStorageLimit = async () => {
    const mb = parseInt(storageEditMb, 10);
    if (!mb || mb <= 0) { toast.error('Ingresa un número de MB mayor a 0'); return; }
    setStorageSaving(true);
    try {
      await api.patch(`/admin/companies/${storageEditCompany.id}/storage-limit`, { storage_limit_mb: mb });
      toast.success(`Límite de almacenamiento de ${storageEditCompany.name} actualizado a ${mb}MB`);
      setStorageEditCompany(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar el límite');
    } finally {
      setStorageSaving(false);
    }
  };

  // Rollout gradual de Biblioteca/R2 (Nivel 2 por-tenant, ver TICKET_BIBLIOTECA_R2_ROLLOUT.md) —
  // vive en el mismo dialog que el límite de almacenamiento (misma pantalla de config de Biblioteca
  // por-empresa, no una pantalla aparte, CLAUDE.md §3.7b).
  const toggleBibliotecaR2 = async (enabled) => {
    setBiblioR2Saving(true);
    try {
      await api.patch(`/admin/companies/${storageEditCompany.id}/biblioteca-r2`, { enabled });
      toast.success(`Biblioteca (R2) ${enabled ? 'encendida' : 'apagada'} para ${storageEditCompany.name}`);
      setStorageEditCompany(prev => prev && { ...prev, biblioteca_r2_enabled: enabled });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar');
    } finally {
      setBiblioR2Saving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#A5D62C] border-t-transparent rounded-full animate-spin" /></div>;

  const statCards = [
    { title: 'Cuentas', value: stats?.total_companies || 0, icon: Building2, color: 'text-[#A5D62C]', bg: 'bg-[#A5D62C]/10' },
    { title: 'Usuarios', value: stats?.total_users || 0, icon: Users, color: 'text-blue-400', bg: 'bg-blue-400/10' },
    { title: 'Propiedades', value: stats?.total_properties || 0, icon: Building2, color: 'text-violet-400', bg: 'bg-violet-400/10' },
    { title: 'Leads', value: stats?.total_leads || 0, icon: Target, color: 'text-amber-400', bg: 'bg-amber-400/10' },
    { title: 'Oportunidades', value: stats?.total_opportunities || 0, icon: BarChart3, color: 'text-pink-400', bg: 'bg-pink-400/10' },
    { title: 'Solicitudes IA', value: stats?.total_ai_requests || 0, icon: Bot, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
  ];

  const btypeStats = stats?.by_business_type || {};

  return (
    <div data-testid="super-admin-page">
      {/* Auto-load data on mount based on current tab */}
      <AutoLoader tab={initialTab} loaders={{
        dynplans: () => { if (!dynPlans.length) loadDynPlans(); },
        backoffice: () => { if (!platformIntegrations) loadPlatformIntegrations(); },
        audit: () => { loadAuditLogs(auditCompanyId); },
      }} />
      <div className="flex items-center justify-between mb-6 sm:mb-8 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">Panel Super Admin</h2>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">Control total del sistema {APP_NAME}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowCreate(true)} className="bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold rounded-lg text-xs h-8" data-testid="create-company-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Nueva Cuenta
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6">
        {statCards.map((card, i) => (
          <div key={card.title} className="stat-card p-4 animate-fade-in-up" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`admin-stat-${card.title.toLowerCase().replace(/ /g,'-')}`}>
            <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center mb-3`}>
              <card.icon className={`w-4 h-4 ${card.color}`} />
            </div>
            <p className="text-2xl font-bold text-white font-heading">{card.value}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wider">{card.title}</p>
          </div>
        ))}
      </div>

      {/* Business Type Breakdown */}
      <div className="flex gap-3 mb-6 flex-wrap">
        {Object.entries(btypeStats).map(([k, v]) => (
          <div key={k} className={`px-3 py-1.5 rounded-lg border ${BTYPE_COLORS[k] || 'border-white/10 text-muted-foreground'} text-xs font-medium`}>
            {BTYPE_LABELS[k] || k}: <span className="font-bold">{v}</span>
          </div>
        ))}
      </div>

      <Tabs value={initialTab} className="w-full">
        {/* Tab list hidden - navigation via BackOffice sidebar */}

        {/* TAB: Companies */}
        <TabsContent value="companies">
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="companies-table">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading text-lg font-semibold text-white">Cuentas Registradas</h3>
                <Badge variant="outline" className="text-xs border-white/10 text-muted-foreground">{stats?.companies?.length || 0} total</Badge>
              </div>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="admin-search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Buscar por nombre o email..."
                  className="pl-9 bg-black/20 border-white/10 focus:border-primary"
                />
              </div>
              {filteredCompanies.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 mx-auto text-muted-foreground/20 mb-4" />
                  <p className="text-muted-foreground text-sm mb-4">{search ? 'No se encontraron resultados.' : 'No hay cuentas activas.'}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="hidden lg:grid grid-cols-9 gap-3 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                    <span className="col-span-2">Cuenta</span>
                    <span className="text-center">Giro / Tipo</span>
                    <span className="text-center">Plan</span>
                    <span className="text-center">Usuarios</span>
                    <span className="text-center">Propiedades</span>
                    <span className="text-center">Leads</span>
                    <span className="text-center">Opps</span>
                    <span className="text-center">Acciones</span>
                  </div>
                  {filteredCompanies.map(c => {
                    const cPlan = c.subscription?.plan_dynamic_id || c.subscription?.plan || '';
                    const cPlanObj = (dynPlans || []).find(p => p.id === cPlan);
                    const cPlanLabel = cPlanObj?.name || c.subscription?.plan_name || cPlan || 'Sin plan';
                    const companyPlanOptions = (dynPlans || [])
                      .filter(p => p?.is_active)
                      .filter(p => {
                        const vis = Array.isArray(p?.visible_for_types) ? p.visible_for_types : [];
                        if (!vis.length) return true;
                        return vis.includes(c.business_type || 'agency') || (!!c.industry && vis.includes(c.industry));
                      })
                      .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));
                    return (
                    <div key={c.id} className="grid grid-cols-1 lg:grid-cols-9 gap-2 lg:gap-3 items-center px-4 py-3 rounded-lg bg-black/20 border border-white/5 hover:border-white/10 transition-colors" data-testid={`company-row-${c.id}`}>
                      <div className="col-span-1 lg:col-span-2 flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${BTYPE_COLORS[c.business_type] || BTYPE_COLORS.agency}`}>
                          {c.business_type === 'developer' ? 'D' : c.business_type === 'independent' ? 'I' : c.business_type === 'franchise' ? 'F' : c.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-white truncate">{c.name}</p>
                          <CopyableId id={c.id} />
                          <p className="text-[10px] text-muted-foreground truncate">{c.contact_email}</p>
                        </div>
                      </div>
                      <div className="hidden lg:flex flex-col items-center gap-1">
                        <Select value={c.industry || ''} onValueChange={(v) => handleChangeIndustry(c.id, c.name, c.industry || '', v)}>
                          <SelectTrigger className="h-6 text-[10px] border border-white/10 bg-transparent w-28 mx-auto" data-testid={`industry-select-${c.id}`}>
                            <SelectValue placeholder="Sin giro">{INDUSTRY_LABELS[c.industry] || 'Sin giro'}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {INDUSTRY_OPTIONS.map(io => (
                              <SelectItem key={io.value} value={io.value} data-testid={`industry-opt-${c.id}-${io.value}`}>{io.icon} {io.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={c.business_type || 'agency'} onValueChange={(v) => handleChangeLicense(c.id, c.name, c.business_type || 'agency', v)}>
                          <SelectTrigger className={`h-6 text-[10px] border ${BTYPE_COLORS[c.business_type] || BTYPE_COLORS.agency} bg-transparent w-28 mx-auto`} data-testid={`license-select-${c.id}`}>
                            <SelectValue>{BTYPE_LABELS[c.business_type] || 'Agencia'}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="agency" data-testid={`license-opt-${c.id}-agency`}>Agencia</SelectItem>
                            <SelectItem value="developer" data-testid={`license-opt-${c.id}-developer`}>Desarrollador</SelectItem>
                            <SelectItem value="independent" data-testid={`license-opt-${c.id}-independent`}>Independiente</SelectItem>
                            <SelectItem value="franchise" data-testid={`license-opt-${c.id}-franchise`}>Franquiciatario</SelectItem>
                            <SelectItem value="owner" data-testid={`license-opt-${c.id}-owner`}>Propietario</SelectItem>
                            <SelectItem value="car_dealer" data-testid={`license-opt-${c.id}-car_dealer`}>Car-Dealer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="text-center hidden lg:block">
                        <Select value={cPlan} onValueChange={async (v) => {
                          try {
                            await api.post(`/admin/plans/${c.id}`, { plan: v });
                            const chosen = companyPlanOptions.find(p => p.id === v);
                            toast.success(`Plan de ${c.name} actualizado a ${chosen?.name || v}`);
                            load();
                          } catch { toast.error('Error al cambiar plan'); }
                        }}>
                          <SelectTrigger className={`h-6 text-[10px] border ${PLAN_COLORS[cPlan] || 'border-white/10'} bg-transparent w-24 mx-auto`} data-testid={`plan-select-${c.id}`}>
                            <SelectValue>{cPlanLabel}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {companyPlanOptions.map(p => (
                              <SelectItem key={p.id} value={p.id} data-testid={`company-plan-option-${c.id}-${p.id}`}>
                                {p.name}
                              </SelectItem>
                            ))}
                            {!companyPlanOptions.length && (
                              <SelectItem value="__no_dynamic_plans__" disabled data-testid={`company-plan-option-empty-${c.id}`}>
                                Sin planes dinámicos
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-sm text-white text-center hidden lg:block">{c.users_count}</p>
                      <p className="text-sm text-white text-center hidden lg:block">{c.properties_count}</p>
                      <p className="text-sm text-white text-center hidden lg:block">{c.leads_count}</p>
                      <p className="text-sm text-white text-center hidden lg:block">{c.opportunities_count}</p>
                      {/* Movil: Tipo y Plan EDITABLES (en escritorio son hidden lg:block) */}
                      <div className="lg:hidden flex flex-col gap-2 text-[10px] text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <span className="w-12 shrink-0 text-white/50">Giro</span>
                          <Select value={c.industry || ''} onValueChange={(v) => handleChangeIndustry(c.id, c.name, c.industry || '', v)}>
                            <SelectTrigger className="h-8 text-[11px] border border-white/10 bg-transparent flex-1" data-testid={`industry-select-m-${c.id}`}>
                              <SelectValue placeholder="Sin giro">{INDUSTRY_LABELS[c.industry] || 'Sin giro'}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {INDUSTRY_OPTIONS.map(io => (
                                <SelectItem key={io.value} value={io.value} data-testid={`industry-opt-m-${c.id}-${io.value}`}>{io.icon} {io.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-12 shrink-0 text-white/50">Tipo</span>
                          <Select value={c.business_type || 'agency'} onValueChange={(v) => handleChangeLicense(c.id, c.name, c.business_type || 'agency', v)}>
                            <SelectTrigger className={`h-8 text-[11px] border ${BTYPE_COLORS[c.business_type] || BTYPE_COLORS.agency} bg-transparent flex-1`} data-testid={`license-select-m-${c.id}`}>
                              <SelectValue>{BTYPE_LABELS[c.business_type] || 'Agencia'}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="agency" data-testid={`license-opt-m-${c.id}-agency`}>Agencia</SelectItem>
                              <SelectItem value="developer" data-testid={`license-opt-m-${c.id}-developer`}>Desarrollador</SelectItem>
                              <SelectItem value="independent" data-testid={`license-opt-m-${c.id}-independent`}>Independiente</SelectItem>
                              <SelectItem value="franchise" data-testid={`license-opt-m-${c.id}-franchise`}>Franquiciatario</SelectItem>
                              <SelectItem value="owner" data-testid={`license-opt-m-${c.id}-owner`}>Propietario</SelectItem>
                              <SelectItem value="car_dealer" data-testid={`license-opt-m-${c.id}-car_dealer`}>Car-Dealer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-12 shrink-0 text-white/50">Plan</span>
                          <Select value={cPlan} onValueChange={async (v) => {
                            try {
                              await api.post(`/admin/plans/${c.id}`, { plan: v });
                              const chosen = companyPlanOptions.find(p => p.id === v);
                              toast.success(`Plan de ${c.name} actualizado a ${chosen?.name || v}`);
                              load();
                            } catch { toast.error('Error al cambiar plan'); }
                          }}>
                            <SelectTrigger className={`h-8 text-[11px] border ${PLAN_COLORS[cPlan] || 'border-white/10'} bg-transparent flex-1`} data-testid={`plan-select-m-${c.id}`}>
                              <SelectValue>{cPlanLabel}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {companyPlanOptions.map(p => (
                                <SelectItem key={p.id} value={p.id} data-testid={`company-plan-option-m-${c.id}-${p.id}`}>
                                  {p.name}
                                </SelectItem>
                              ))}
                              {!companyPlanOptions.length && (
                                <SelectItem value="__no_dynamic_plans__" disabled data-testid={`company-plan-option-m-empty-${c.id}`}>
                                  Sin planes dinamicos
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-3 text-[9px]">
                          <span>{c.users_count} usuarios</span>
                          <span>{c.leads_count} leads</span>
                        </div>
                      </div>
                      <div className="flex justify-center items-center gap-1">
                        <div
                          className="flex flex-col items-center justify-center px-1"
                          title={c.comm_logics_cascade_activated_at
                            ? `Seguimientos activados desde ${new Date(c.comm_logics_cascade_activated_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`
                            : 'Seguimientos apagados'}
                          data-testid={`cascade-toggle-wrap-${c.id}`}
                        >
                          <Switch
                            checked={!!c.comm_logics_cascade_activated_at}
                            onCheckedChange={() => handleToggleCascade(c.id, c.name, !!c.comm_logics_cascade_activated_at)}
                            className="scale-75"
                            data-testid={`cascade-toggle-${c.id}`}
                          />
                          <span className="text-[8px] text-muted-foreground leading-none mt-0.5 whitespace-nowrap">Seguimientos</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => openStorageEdit(c)} className="text-orange-400/60 hover:text-orange-300 hover:bg-orange-500/10 h-8 w-8 p-0" title="Biblioteca: encendido y límite de almacenamiento" data-testid={`storage-limit-btn-${c.id}`}>
                          <HardDrive className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setVozForm({ phone_number: '', account_sid: '', auth_token: '' }); setVozTenant(c); }} className="text-emerald-400/60 hover:text-emerald-300 hover:bg-emerald-500/10 h-8 w-8 p-0" title="Voz IA con Twilio propio del tenant" data-testid={`voz-tenant-btn-${c.id}`}>
                          <Phone className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleImpersonate(c.id)} className="text-[#A5D62C]/60 hover:text-[#A5D62C] hover:bg-[#A5D62C]/10 h-8 w-8 p-0" title="Ver como esta cuenta" data-testid={`impersonate-${c.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleConfigureAI(c.id, '/ai-agent')} className="text-sky-400/60 hover:text-sky-300 hover:bg-sky-500/10 h-8 w-8 p-0" title="Configurar Agente IA de esta cuenta" data-testid={`config-ai-agent-${c.id}`}>
                          <Bot className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleConfigureAI(c.id, '/campana-ia-independiente')} className="text-violet-400/60 hover:text-violet-300 hover:bg-violet-500/10 h-8 w-8 p-0" title="Configurar Campañas IA de esta cuenta" data-testid={`config-ai-campaigns-${c.id}`}>
                          <Sparkles className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleSuspend(c.id, c.name)} className="text-amber-400/70 hover:text-amber-300 hover:bg-amber-500/10 h-8 w-8 p-0" title="Suspender cuenta" data-testid={`suspend-company-${c.id}`}>
                          <PauseCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );})}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: AI Usage */}
        <TabsContent value="ai">
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="admin-ai-usage">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white">Usabilidad por Tenant</h3>
                  <p className="text-xs text-muted-foreground">Lo que te cuesta cada cuenta: IA, voz, número Twilio, WhatsApp y memoria. El cliente no ve dólares.</p>
                </div>
                <Button variant="outline" size="sm" onClick={load} className="border-white/10 text-muted-foreground text-xs">Actualizar</Button>
              </div>
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4 mb-6" data-testid="or-wallet">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <div>
                    <p className="text-sm font-bold text-emerald-300">Billetera OpenRouter</p>
                    <p className="text-[10px] text-muted-foreground mt-1 max-w-xl">
                      {orWallet?.connected
                        ? 'Toda la IA (chat y voz) se paga aquí, con el modelo más barato que sirva para vender. Twilio solo pone el teléfono: oír y hablar. No usamos Realtime de OpenAI — es caro y no pasa por OpenRouter.'
                        : (orWallet?.mensaje || 'Cargando saldo...')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-1 rounded border ${orWallet?.connected ? 'border-emerald-400/40 text-emerald-300' : 'border-white/10 text-muted-foreground'}`}>
                      {orWallet?.connected ? 'Conectado' : 'Sin llave'}
                    </span>
                    <Button variant="outline" size="sm" onClick={loadOpenRouter} className="border-white/10 text-muted-foreground text-xs">Actualizar saldo</Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-lg bg-black/20 p-3">
                    <p className="text-xl font-bold text-white font-heading">{orWallet?.connected ? `$${(orWallet.saldo_usd ?? 0).toFixed(2)}` : '--'}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Saldo disponible</p>
                  </div>
                  <div className="rounded-lg bg-black/20 p-3">
                    <p className="text-xl font-bold text-white font-heading">{orWallet?.connected ? `$${(orWallet.usado_usd ?? 0).toFixed(2)}` : '--'}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Gastado</p>
                  </div>
                  <div className="rounded-lg bg-black/20 p-3">
                    <p className="text-sm font-semibold text-white truncate">{orWallet?.modelo_activo || 'Todavia no hay modelo'}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Modelo mas barato ahora. Si falla: {(orWallet?.reemplazos || []).join(' - ') || '--'}
                    </p>
                  </div>
                </div>
              </div>
              {isPlatformOwner(user) && (
                <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-4 mb-6" data-testid="admin-infra-costo">
                  <p className="text-sm font-bold text-violet-200">Costo de infraestructura</p>
                  <p className="text-[10px] text-muted-foreground mt-1 max-w-xl">
                    Se divide entre los tenants activos y se prorratea al día del mes. Si entra uno nuevo, se diluye. Solo tú lo ponderas.
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <Input
                      type="number"
                      value={infraMxn}
                      onChange={(e) => setInfraMxn(Number(e.target.value))}
                      className="w-40 bg-black/20 border-white/10 text-xs h-8"
                      data-testid="admin-infra-mxn"
                    />
                    <span className="text-[10px] text-muted-foreground">MXN / mes</span>
                    <Button
                      size="sm"
                      disabled={infraSaving}
                      className="h-8 text-[11px]"
                      onClick={async () => {
                        setInfraSaving(true);
                        try {
                          const r = await api.put('/admin/ai/infra', { infra_mxn_mes: infraMxn });
                          setInfraMxn(r.data.infra_mxn_mes);
                          toast.success(`Infra: ${Number(r.data.infra_mxn_mes).toLocaleString('es-MX')} MXN ÷ ${r.data.n_activos} activos`);
                          if (aiTenantId) {
                            const u = await api.get(`/admin/ai/usabilidad/${aiTenantId}`, { params: { dias: aiDias } });
                            setUsabilidad(u.data);
                          }
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'No se pudo guardar');
                        } finally { setInfraSaving(false); }
                      }}
                    >
                      Guardar
                    </Button>
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 mb-6" data-testid="admin-ai-tenant-costos">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <div>
                    <p className="text-sm font-bold text-amber-200">Radiografía del tenant</p>
                    <p className="text-[10px] text-muted-foreground mt-1 max-w-xl">
                      Consumo del periodo + fijos del mes. El cliente no ve dólares: esto es lo que te cuesta a ti.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer" data-testid="admin-ai-inactivos">
                      <Switch checked={showInactivos} onCheckedChange={setShowInactivos} className="scale-75" />
                      Mostrar inactivos y cancelados
                    </label>
                    <Select value={String(aiDias)} onValueChange={(v) => setAiDias(Number(v))}>
                      <SelectTrigger className="w-[110px] bg-black/20 border-white/10 text-xs" data-testid="admin-ai-dias">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 días</SelectItem>
                        <SelectItem value="30">30 días</SelectItem>
                        <SelectItem value="90">90 días</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={aiTenantId} onValueChange={setAiTenantId}>
                      <SelectTrigger className="w-[240px] bg-black/20 border-white/10 text-xs" data-testid="admin-ai-tenant-select">
                        <SelectValue placeholder="Elige una empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        {(aiTenants || []).map((c) => (
                          <SelectItem key={c.company_id} value={c.company_id}>
                            {c.activa === false ? `(inactiva) ${c.nombre}` : c.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {usabilidad ? (
                  <div className="space-y-3" data-testid="admin-ai-tabla-costos">
                    <div className="overflow-x-auto rounded-lg border border-white/10">
                      <table className="w-full text-xs">
                        <thead className="bg-black/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                          <tr>
                            <th className="text-left p-2 font-medium">Concepto</th>
                            <th className="text-left p-2 font-medium">Periodo</th>
                            <th className="text-right p-2 font-medium">Uso</th>
                            <th className="text-left p-2 font-medium">Tarifa</th>
                            <th className="text-right p-2 font-medium">Costo USD</th>
                            <th className="text-left p-2 font-medium">Nota</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { key: 'ia', titulo: 'IA' },
                            { key: 'llamadas', titulo: 'Llamadas' },
                            { key: 'mensajeria', titulo: 'Mensajería WhatsApp' },
                            { key: 'fijo', titulo: 'Fijos del mes (prorrateados al día)' },
                          ].map((g) => {
                            const filasG = (usabilidad.filas || []).filter((f) => f.grupo === g.key);
                            if (!filasG.length) return null;
                            const sub = filasG.reduce((a, f) => a + Number(f.costo_usd || 0), 0);
                            return (
                              <React.Fragment key={g.key}>
                                <tr className="border-t border-white/10 bg-white/5">
                                  <td className="p-2 text-[10px] uppercase tracking-wider text-muted-foreground font-bold" colSpan={6}>{g.titulo}</td>
                                </tr>
                                {filasG.map((f) => (
                                  <tr key={f.concepto} className="border-t border-white/5">
                                    <td className="p-2 text-white">{f.concepto}</td>
                                    <td className="p-2 text-muted-foreground">{f.periodo === 'ventana' ? `${usabilidad.ventana_dias || aiDias} días` : f.periodo}</td>
                                    <td className="p-2 text-right font-mono text-white/80">{f.cantidad} {f.unidad}</td>
                                    <td className="p-2 text-muted-foreground">{f.tarifa}</td>
                                    <td className="p-2 text-right font-mono text-white">{usd(f.costo_usd)}</td>
                                    <td className="p-2 text-muted-foreground max-w-[220px] truncate" title={f.nota}>{f.nota}</td>
                                  </tr>
                                ))}
                                <tr className="border-t border-white/10 bg-black/20">
                                  <td className="p-2 text-white/80 font-semibold" colSpan={4}>Subtotal {g.titulo}</td>
                                  <td className="p-2 text-right font-mono text-white font-semibold">{usd(sub)}</td>
                                  <td />
                                </tr>
                              </React.Fragment>
                            );
                          })}
                          <tr className="border-t border-emerald-500/30 bg-emerald-500/10">
                            <td className="p-2 text-emerald-200 font-bold" colSpan={4}>Total LeadCEM</td>
                            <td className="p-2 text-right font-mono text-emerald-300 font-bold">{usd(usabilidad.total_usd)}</td>
                            <td className="p-2 text-[10px] text-emerald-200/70">el tenant no ve dólares</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {(usabilidad.notas || []).length > 0 && (
                      <p className="text-[10px] text-muted-foreground">{usabilidad.notas[usabilidad.notas.length - 1]}</p>
                    )}
                    {vozIa && (
                      <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-3 space-y-2" data-testid="admin-voz-motor">
                        <p className="text-xs font-bold text-sky-200">Motor de voz de este tenant</p>
                        <p className="text-[10px] text-muted-foreground">
                          A es de serie (Polly Neural, barato). B es conversación en vivo: se le interrumpe. B exige un plan mejor y la llave en BackOffice → Voz en vivo.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant={vozIa.motor_pedido === 'gather' ? 'default' : 'outline'}
                            disabled={vozSaving}
                            className="text-[11px] h-8"
                            data-testid="voz-motor-a"
                            onClick={async () => {
                              setVozSaving(true);
                              try {
                                const r = await api.put(`/admin/ai/voz/${aiTenantId}`, { motor: 'gather' });
                                setVozIa({ ...vozIa, ...r.data });
                                toast.success('Motor A (Neural). Suena mejor, sigue por turnos.');
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'No se pudo cambiar');
                              } finally { setVozSaving(false); }
                            }}
                          >
                            A · Neural
                          </Button>
                          <Button
                            size="sm"
                            variant={vozIa.motor_pedido === 'realtime' ? 'default' : 'outline'}
                            disabled={vozSaving}
                            className="text-[11px] h-8"
                            data-testid="voz-motor-b"
                            onClick={async () => {
                              setVozSaving(true);
                              try {
                                const r = await api.put(`/admin/ai/voz/${aiTenantId}`, { motor: 'realtime' });
                                setVozIa({ ...vozIa, ...r.data });
                                if (r.data?.aviso) toast.message(r.data.aviso);
                                else toast.success('Motor B (en vivo). Se puede interrumpir.');
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'No se pudo cambiar');
                              } finally { setVozSaving(false); }
                            }}
                          >
                            B · En vivo
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <p className="text-[10px] text-muted-foreground mb-1">Voz</p>
                            <Select
                              value={vozIa.ia_voice || 'ara'}
                              onValueChange={async (v) => {
                                setVozSaving(true);
                                try {
                                  const r = await api.put(`/admin/ai/voz/${aiTenantId}`, { ia_voice: v });
                                  setVozIa({ ...vozIa, ...r.data });
                                  toast.success('Voz actualizada');
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'No se pudo guardar la voz');
                                } finally { setVozSaving(false); }
                              }}
                            >
                              <SelectTrigger className="h-8 bg-black/20 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(vozIa.voces || [
                                  { id: 'ara', label: 'Ara · cálida (mujer)' },
                                  { id: 'eve', label: 'Eve · energética' },
                                  { id: 'leo', label: 'Leo · firme (hombre)' },
                                  { id: 'rex', label: 'Rex · clara' },
                                  { id: 'sal', label: 'Sal · suave' },
                                ]).map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <p className="text-[10px] text-muted-foreground mb-1">Temperatura {Number(vozIa.ia_temperature ?? 0.55).toFixed(2)}</p>
                            <input
                              type="range" min="0.1" max="1" step="0.05"
                              className="w-full"
                              value={Number(vozIa.ia_temperature ?? 0.55)}
                              onChange={(e) => setVozIa({ ...vozIa, ia_temperature: Number(e.target.value) })}
                              onMouseUp={async (e) => {
                                const t = Number(e.target.value);
                                try {
                                  const r = await api.put(`/admin/ai/voz/${aiTenantId}`, { ia_temperature: t });
                                  setVozIa({ ...vozIa, ...r.data });
                                } catch { toast.error('No se guardó la temperatura'); }
                              }}
                            />
                          </div>
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          Pedido: {vozIa.motor_pedido === 'realtime' ? 'B' : 'A'} · En el aire: {vozIa.motor_efectivo === 'realtime' ? 'B en vivo' : 'A Neural'}
                          {vozIa.proveedor_listo ? ` · proveedor ${vozIa.proveedor_plataforma || 'listo'}` : ' · falta llave en Integraciones → Voz en vivo'}
                          {vozIa.razon ? ` · ${vozIa.razon}` : ''}
                        </p>
                      </div>
                    )}
                  </div>
                ) : selectedAiTenant ? (
                  <p className="text-[11px] text-muted-foreground">Cargando radiografía de {selectedAiTenant.nombre}…</p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">Selecciona un tenant activo para ver su costo. El cliente no ve dólares.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Inventario SA5 — Propiedades | Desarrollos */}
        <TabsContent value="properties">
          <InventoryPanel companies={stats?.companies || []} />
        </TabsContent>

        {/* TAB: Reportes SA6 — por vertical */}
        <TabsContent value="reports">
          <ReportsPanel />
        </TabsContent>

        {/* TAB: Dynamic Plans Management */}
        <TabsContent value="dynplans">
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="dynplans-panel">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2"><Package className="w-5 h-5 text-amber-400" /> Gestion de Planes</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Configura los planes de suscripcion con limites, precios y periodos de prueba</p>
                </div>
                <Button onClick={() => openPlanEditor(null)} className="bg-amber-600 hover:bg-amber-500 text-xs h-8" data-testid="create-plan-btn">
                  <Plus className="w-3 h-3 mr-1" /> Nuevo Plan
                </Button>
              </div>

              {dynPlansLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
              ) : dynPlans.length === 0 ? (
                <div className="text-center py-12"><Package className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" /><p className="text-sm text-muted-foreground">Haz clic en la pestana para cargar</p></div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  {dynPlans.map(plan => (
                    <div key={plan.id} className={`rounded-xl border p-4 transition-all ${plan.is_popular ? 'border-amber-500/20 bg-amber-500/5' : 'border-white/5 bg-black/20'} ${!plan.is_active ? 'opacity-50' : ''}`} data-testid={`dynplan-${plan.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium text-white">{plan.name}</p>
                          <p className="text-[10px] text-muted-foreground">{LICENSE_TYPE_OPTIONS.find((opt) => opt.value === (plan.license_type || plan.target_audience))?.label || 'Agencia'}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {plan.is_visible ? <Badge className="text-[8px] bg-emerald-500/10 text-emerald-400">Visible</Badge> : <Badge className="text-[8px] bg-red-500/10 text-red-400">Oculto</Badge>}
                          {plan.is_popular && <Badge className="text-[8px] bg-amber-500/10 text-amber-400">Popular</Badge>}
                          {plan.is_seed && <Badge className="text-[8px] bg-violet-500/10 text-violet-400">Sistema</Badge>}
                        </div>
                      </div>
                      {/* Visible for types badges */}
                      {(plan.visible_for_types || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(plan.visible_for_types || []).map(t => (
                            <span key={t} className="text-[8px] px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20">
                              {t === 'agency' ? 'Agencias' : t === 'developer' ? 'Desarrolladores' : t === 'franchise' ? 'Franquicias' : t === 'owner' ? 'Owner' : t === 'car_dealer' ? 'Car-Dealer' : 'Independientes'}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mb-2">
                        {plan.original_price > plan.price && <span className="text-[10px] text-muted-foreground line-through mr-1">${plan.original_price}</span>}
                        <span className="text-lg font-bold text-amber-400">${plan.price}</span>
                        <span className="text-[10px] text-muted-foreground ml-0.5">{plan.currency}/{plan.billing_period === 'month' ? 'mes' : plan.billing_period === 'quarter' ? 'trim' : 'anual'}</span>
                        {plan.discount_pct > 0 && <Badge className="text-[8px] bg-orange-500/10 text-orange-400 ml-1">{plan.discount_pct}% dto</Badge>}
                      </div>
                      <div className="grid grid-cols-2 gap-1 mb-2 text-[9px]" data-testid={`plan-matrix-${plan.id}`}>
                        {(() => {
                          const matrix = getPriceMatrix(plan);
                          const volumeDiscount = Number(plan?.superadmin_volume_pricing?.discount_pct || 0);
                          const applyDiscount = (amount) => Number(amount || 0) * (1 - (volumeDiscount / 100));
                          return (
                            <>
                              <div className="rounded bg-black/20 border border-white/10 px-2 py-1">Mensual: ${matrix.monthly.toLocaleString()}</div>
                              <div className="rounded bg-black/20 border border-white/10 px-2 py-1">Trimestral: ${matrix.quarterly.toLocaleString()}</div>
                              <div className="rounded bg-black/20 border border-white/10 px-2 py-1">Semestral: ${matrix.semiannual.toLocaleString()}</div>
                              <div className="rounded bg-black/20 border border-white/10 px-2 py-1">Anual: ${matrix.annual.toLocaleString()}</div>
                              {volumeDiscount > 0 && (
                                <div className="col-span-2 rounded bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 text-emerald-300" data-testid={`plan-volume-discount-${plan.id}`}>
                                  SuperAdmin volumen: -{volumeDiscount}% (base mensual ${applyDiscount(matrix.monthly).toLocaleString()})
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      {plan.trial_days > 0 && <p className="text-[10px] text-emerald-400/70 mb-2">{plan.trial_days} dias de prueba</p>}
                      <p className="text-[10px] text-white/70 mb-2" data-testid={`plan-caps-chip-${plan.id}`}>
                        Leads/mes: {capLabel(plan.monthly_caps?.contacts_monthly ?? plan.limits?.contacts)}
                        {plan.capabilities?.voice === false ? ' · sin voz' : ''}
                      </p>
                      <div className="grid grid-cols-3 gap-1 mb-2 text-center">
                        {[{k:'users',l:'Usr'},{k:'flows',l:'Flujos'},{k:'campaigns',l:'Camp.'},{k:'developments',l:'Desarr.'}].map(lim => (
                          <div key={lim.k} className="p-1 rounded bg-white/5 text-[9px]">
                            <span className="text-white font-bold">{plan.limits?.[lim.k] ?? '∞'}</span> <span className="text-muted-foreground">{lim.l}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-1 mt-3">
                        <Button variant="outline" size="sm" onClick={() => openPlanEditor(plan)} className="flex-1 text-[10px] h-7 border-white/10" data-testid={`edit-plan-${plan.id}`}>
                          <Pencil className="w-3 h-3 mr-0.5" /> Editar
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => deletePlan(plan)} className="text-[10px] h-7 border-red-500/20 text-red-400 hover:bg-red-500/10" data-testid={`delete-plan-${plan.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <PlanEditorDialog
            open={planDialog}
            onOpenChange={setPlanDialog}
            plan={editingPlan}
            sortOrder={dynPlans.length + 1}
            onSaved={loadDynPlans}
          />
        </TabsContent>

        {/* TAB: Integraciones de plataforma (ex BackOffice / ex Plataforma) */}
        <TabsContent value="backoffice">
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="backoffice-panel">
            <CardContent className="p-4 sm:p-6">
              <div className="mb-5">
                <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-pink-400" /> Integraciones
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Una sola pantalla para las llaves de la plataforma: Twilio, Calendly, Meta, Gupshup, OpenRouter, calendarios y pagos. El tenant no las ve. El rayo prueba sin mandar mensajes.
                </p>
              </div>

              {platformLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-pink-400 animate-spin" /></div>
              ) : (
                <IntegrationsBoard
                  items={platformIntegrations}
                  onConfigure={openInteg}
                  onRemove={removePlatformIntegration}
                  onTest={testInteg}
                  testing={testingInteg}
                />
              )}
            </CardContent>
          </Card>

          {/* Integration Configuration Dialog */}
          <Dialog open={!!integrationDialog} onOpenChange={(v) => { if (!v) setIntegrationDialog(null); }}>
            <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto bg-[#0a0c12] border-white/[0.06]" data-testid="integration-config-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-pink-400 flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  {platformIntegrations?.[integrationDialog]?.name || 'Integracion'}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {platformIntegrations?.[integrationDialog]?.description}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Calendly special guide */}
                {integrationDialog === 'calendly' && calendlyGuide && (
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 space-y-3" data-testid="calendly-guide">
                    <p className="text-xs font-bold text-amber-400 flex items-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" /> {calendlyGuide.title}
                    </p>
                    {calendlyGuide.steps.map(step => (
                      <div key={step.step} className="space-y-1">
                        <p className="text-[11px] font-semibold text-white/80">Paso {step.step}: {step.title}</p>
                        <ul className="space-y-0.5">
                          {step.instructions.map((inst, i) => (
                            <li key={i} className="text-[10px] text-muted-foreground flex gap-1.5">
                              <span className="text-amber-400/60 shrink-0">{i + 1}.</span> {inst}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    {calendlyGuide.notes && (
                      <div className="border-t border-amber-500/10 pt-2 mt-2 space-y-1">
                        <p className="text-[10px] font-semibold text-amber-400/80">Notas importantes:</p>
                        {calendlyGuide.notes.map((note, i) => (
                          <p key={i} className="text-[10px] text-muted-foreground">- {note}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Credential fields based on integration type */}
                {integrationDialog === 'calendly' && (
                  <div className="space-y-3">
                    <div><Label className="text-[10px]">Client ID</Label><Input value={integrationCreds.client_id || ''} onChange={e => setIntegrationCreds({...integrationCreds, client_id: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Ej: abc123..." data-testid="integ-calendly-client-id" /></div>
                    <div><Label className="text-[10px]">Client Secret</Label><Input type="password" value={integrationCreds.client_secret || ''} onChange={e => setIntegrationCreds({...integrationCreds, client_secret: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Ej: sec_..." data-testid="integ-calendly-secret" /></div>
                    <div><Label className="text-[10px]">Webhook Signing Key</Label><Input type="password" value={integrationCreds.webhook_signing_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, webhook_signing_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Ej: eyJhbGci..." data-testid="integ-calendly-signing-key" /></div>
                    <div className="p-2 rounded bg-amber-500/5 border border-amber-500/10">
                      <p className="text-[9px] text-amber-400/80">El Webhook Signing Key se encuentra en developer.calendly.com &gt; tu app &gt; Webhooks. Se usa para verificar que los eventos recibidos son autenticos.</p>
                    </div>
                    <div>
                      <Label className="text-[10px]">Redirect URI (auto-generado)</Label>
                      <div className="flex items-center gap-1 mt-1">
                        <Input readOnly value={calendlyRedirectUri || '⚠️ Configura FRONTEND_URL'} className="bg-black/30 border-white/10 text-xs font-mono text-blue-400/80" data-testid="integ-calendly-redirect" />
                        <button type="button" onClick={() => { navigator.clipboard.writeText(calendlyRedirectUri); toast.success('URI copiada'); }} className="shrink-0 p-1.5 rounded hover:bg-white/5 text-blue-400/60 hover:text-blue-400">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        </button>
                      </div>
                      <p className="text-[9px] text-muted-foreground mt-1">Copia esta URI y pegala en el campo "Redirect URI" de tu app OAuth en developer.calendly.com</p>
                    </div>
                  </div>
                )}
                {(integrationDialog === 'google_calendar' || integrationDialog === 'outlook_calendar') && (
                  <div className="space-y-3">
                    <div><Label className="text-[10px]">Client ID</Label><Input value={integrationCreds.client_id || ''} onChange={e => setIntegrationCreds({...integrationCreds, client_id: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={integrationDialog === 'google_calendar' ? 'Ej: 1234-abc.apps.googleusercontent.com' : 'Ej: 1111-2222-3333-4444-5555 (Application/Client ID)'} data-testid="integ-cal-client-id" /></div>
                    <div><Label className="text-[10px]">Client Secret</Label><Input type="password" value={integrationCreds.client_secret || ''} onChange={e => setIntegrationCreds({...integrationCreds, client_secret: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={platformIntegrations?.[integrationDialog]?.configured ? '(dejar vacio conserva el valor guardado)' : (integrationDialog === 'google_calendar' ? 'Ej: GOCSPX-...' : 'Ej: abc8Q~... (el Value del secret, no el ID)')} data-testid="integ-cal-secret" /></div>
                    <div>
                      <Label className="text-[10px]">Redirect URI (URI de redireccionamiento autorizado)</Label>
                      <div className="flex items-center gap-1 mt-1">
                        <Input value={integrationCreds.redirect_uri || ''} onChange={e => setIntegrationCreds({...integrationCreds, redirect_uri: e.target.value})} className="bg-black/20 border-white/10 text-xs font-mono" placeholder={platformBase ? `${platformBase}/api/integrations/calendar/${integrationDialog === 'google_calendar' ? 'google' : 'outlook'}/callback` : '⚠️ Configura la URL de plataforma'} data-testid="integ-cal-redirect" />
                        <button type="button" onClick={() => { const u = integrationCreds.redirect_uri || (platformBase ? `${platformBase}/api/integrations/calendar/${integrationDialog === 'google_calendar' ? 'google' : 'outlook'}/callback` : ''); navigator.clipboard.writeText(u); toast.success('URI copiada'); }} className="shrink-0 p-1.5 rounded hover:bg-white/5 text-blue-400/60 hover:text-blue-400">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        </button>
                      </div>
                      <p className="text-[9px] text-muted-foreground mt-1">Debe coincidir EXACTO con el URI que registres en {integrationDialog === 'google_calendar' ? 'Google Cloud Console (OAuth 2.0 Client)' : 'Azure Portal (App registration › Authentication)'}. Sugerido: <span className="font-mono text-blue-400/70">{platformBase ? `${platformBase}/api/integrations/calendar/${integrationDialog === 'google_calendar' ? 'google' : 'outlook'}/callback` : '(configura la URL de plataforma)'}</span></p>
                    </div>
                  </div>
                )}
                {integrationDialog === 'meta_app' && (
                  <div className="space-y-3">
                    <div><Label className="text-[10px]">App ID (publico)</Label><Input value={integrationCreds.app_id || ''} onChange={e => setIntegrationCreds({...integrationCreds, app_id: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder="Ej: 967846782313486" data-testid="integ-meta-app-id" /></div>
                    <div><Label className="text-[10px]">App Secret</Label><Input type="password" value={integrationCreds.app_secret || ''} onChange={e => setIntegrationCreds({...integrationCreds, app_secret: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={platformIntegrations?.meta_app?.configured ? '(dejar vacio conserva el valor guardado)' : 'El App Secret de tu app de Meta'} data-testid="integ-meta-secret" /></div>
                    <div><Label className="text-[10px]">Config ID (opcional)</Label><Input value={integrationCreds.config_id || ''} onChange={e => setIntegrationCreds({...integrationCreds, config_id: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder="Opcional — dejar vacio funciona" data-testid="integ-meta-config-id" /></div>
                    <div>
                      <Label className="text-[10px]">Redirect URI (solo lectura — copiala a Meta)</Label>
                      <div className="flex items-center gap-1 mt-1">
                        <Input readOnly value={`${platformBase || 'https://app.leadcem.com'}/api/integrations/meta/leadforms/callback`} className="bg-black/20 border-white/10 text-xs font-mono" data-testid="integ-meta-redirect" />
                        <button type="button" onClick={() => { navigator.clipboard.writeText(`${platformBase || 'https://app.leadcem.com'}/api/integrations/meta/leadforms/callback`); toast.success('URI copiada'); }} className="shrink-0 p-1.5 rounded hover:bg-white/5 text-blue-400/60 hover:text-blue-400">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        </button>
                      </div>
                      <p className="text-[9px] text-muted-foreground mt-1">Pega esta URL EXACTA en Meta → tu app → "Inicio de sesion de Facebook para empresas" → Configuracion → URI de redireccion de OAuth validos.</p>
                    </div>
                  </div>
                )}
{integrationDialog === 'tiktok_app' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-pink-500/20 bg-pink-500/5 p-3 space-y-1" data-testid="tiktok-guide">
                      <p className="text-[11px] font-bold text-pink-400">Como obtener las credenciales de TikTok</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-pink-400/70">1.</span> Entra a <span className="text-white">developers.tiktok.com</span> → Manage apps → crea una app.</p>
                        <p><span className="text-pink-400/70">2.</span> En <span className="text-white">Basic information</span> copia el <span className="text-white">Client key</span> y el <span className="text-white">Client secret</span>.</p>
                        <p><span className="text-pink-400/70">3.</span> Agrega el producto <span className="text-white">Login Kit</span> y pega abajo la Redirect URI EXACTA.</p>
                        <p className="text-pink-400/70 pl-3">Para que la IA atienda DMs/comentarios, TikTok exige aprobar a LeadCEM como Messaging Partner (Business Center). La conexion queda lista antes; el atendido se enciende al aprobar.</p>
                      </div>
                    </div>
                    <div><Label className="text-[10px]">Client Key (publico)</Label><Input value={integrationCreds.client_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, client_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder="El Client Key de tu app de TikTok" data-testid="integ-tiktok-client-key" /></div>
                    <div><Label className="text-[10px]">Client Secret</Label><Input type="password" value={integrationCreds.client_secret || ''} onChange={e => setIntegrationCreds({...integrationCreds, client_secret: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={platformIntegrations?.tiktok_app?.configured ? '(dejar vacio conserva el valor guardado)' : 'El Client Secret de tu app de TikTok'} data-testid="integ-tiktok-secret" /></div>
                    <div><Label className="text-[10px]">Scopes (opcional)</Label><Input value={integrationCreds.scopes || ''} onChange={e => setIntegrationCreds({...integrationCreds, scopes: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder="Ej: user.info.basic,user.info.profile" data-testid="integ-tiktok-scopes" /><p className="text-[9px] text-muted-foreground mt-1">Agrega los scopes de mensajeria/comentarios cuando TikTok los apruebe. Vacio = solo perfil.</p></div>
                    <div>
                      <Label className="text-[10px]">Redirect URI (solo lectura — copiala a TikTok)</Label>
                      <div className="flex items-center gap-1 mt-1">
                        <Input readOnly value={`${platformBase || 'https://app.leadcem.com'}/api/integrations/tiktok/callback`} className="bg-black/20 border-white/10 text-xs font-mono" data-testid="integ-tiktok-redirect" />
                        <button type="button" onClick={() => { navigator.clipboard.writeText(`${platformBase || 'https://app.leadcem.com'}/api/integrations/tiktok/callback`); toast.success('URI copiada'); }} className="shrink-0 p-1.5 rounded hover:bg-white/5 text-blue-400/60 hover:text-blue-400">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        </button>
                      </div>
                      <p className="text-[9px] text-muted-foreground mt-1">Pega esta URL EXACTA en TikTok → tu app → Login Kit → Redirect URI.</p>
                    </div>
                  </div>
                )}

                {integrationDialog === 'cloudflare_turnstile' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3 space-y-2" data-testid="turnstile-guide">
                      <p className="text-[11px] font-bold text-orange-400">Como obtener las claves de Cloudflare Turnstile</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-orange-400/70">1.</span> Entra a <span className="text-white">https://dash.cloudflare.com</span> y en el menu lateral busca <span className="text-white">Turnstile</span></p>
                        <p><span className="text-orange-400/70">2.</span> Clic en <span className="text-white">"Agregar widget"</span>. Ponle de nombre <span className="text-white">LEADCEM</span></p>
                        <p className="text-orange-400/70 pl-3">El nombre del widget es solo una etiqueta para identificarlo; los dominios sí importan y siguen siendo dealider.com</p>
                        <p><span className="text-orange-400/70">3.</span> En <span className="text-white">"Nombres de host"</span> agrega los dos, uno por uno: <span className="text-white">app.dealider.com</span> y <span className="text-white">lab.dealider.com</span></p>
                        <p><span className="text-orange-400/70">4.</span> Modo widget: deja <span className="text-white">Gestionado</span> (el recomendado)</p>
                        <p><span className="text-orange-400/70">5.</span> Deja APAGADO "Omitir futuros desafios": solo sirve si el dominio pasa por el proxy de Cloudflare, y el nuestro no</p>
                        <p><span className="text-orange-400/70">6.</span> Clic en <span className="text-white">Crear</span>. Te muestra las dos claves</p>
                      </div>
                      <div className="border-t border-orange-500/10 pt-2 mt-1">
                        <p className="text-[9px] text-orange-400/70">No hace falta mover el dominio a Cloudflare ni tocar los DNS. Turnstile funciona como componente suelto.</p>
                      </div>
                    </div>
                    <div><Label className="text-[10px]">Site Key (publica)</Label><Input value={integrationCreds.site_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, site_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Ej: 0x4AAAAAAA..." data-testid="integ-turnstile-site-key" /><p className="text-[9px] text-muted-foreground mt-1">Va en la pantalla de entrada. Es publica: no pasa nada si se ve.</p></div>
                    <div><Label className="text-[10px]">Secret Key (privada)</Label><Input type="password" value={integrationCreds.secret_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, secret_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Ej: 0x4AAAAAAA..." data-testid="integ-turnstile-secret-key" /><p className="text-[9px] text-muted-foreground mt-1">Solo vive en el servidor. NUNCA la compartas por chat de grupo.</p></div>
                    <div className="p-2 rounded bg-amber-500/5 border border-amber-500/10">
                      <p className="text-[9px] text-amber-400/80">Mientras no esten las dos claves, la pantalla de entrada sigue usando el mecanismo anterior. No se apaga nada hasta que esto quede configurado y probado.</p>
                    </div>
                  </div>
                )}
                {(integrationDialog === 'twilio_voice' || integrationDialog === 'twilio_messaging' || integrationDialog === 'twilio_whatsapp') && (
                  <div className="space-y-3">
                    {/* Twilio Guide */}
                    <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 space-y-2" data-testid="twilio-guide">
                      <p className="text-[11px] font-bold text-blue-400">Como obtener tus credenciales de Twilio</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-blue-400/70">1.</span> Ingresa a <span className="text-white">https://console.twilio.com</span></p>
                        <p><span className="text-blue-400/70">2.</span> En el Dashboard principal, busca la seccion <span className="text-white">"Account Info"</span></p>
                        <p><span className="text-blue-400/70">3.</span> <span className="text-white">Account SID:</span> Es el codigo que inicia con <span className="text-white">AC</span> (ej: AC6c72763...)</p>
                        <p><span className="text-blue-400/70">4.</span> <span className="text-white">Auth Token:</span> Haz clic en <span className="text-white">"Show"</span> para revelarlo. Es una cadena DIFERENTE al SID (~32 caracteres alfanumericos)</p>
                        <p><span className="text-blue-400/70">5.</span> <span className="text-white">Numero:</span> Ve a Phone Numbers {'>'} Manage {'>'} Active Numbers. Copia el numero en formato E.164 (+52...)</p>
                      </div>
                      <div className="border-t border-blue-500/10 pt-2 mt-1">
                        <p className="text-[9px] text-blue-400/70">Importante: El Auth Token NO es igual al Account SID. Si ves error 401, verifica que copiaste el Auth Token correcto.</p>
                      </div>
                    </div>
                    <div><Label className="text-[10px]">Account SID</Label><Input value={integrationCreds.account_sid || ''} onChange={e => setIntegrationCreds({...integrationCreds, account_sid: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="AC..." data-testid="integ-twilio-sid" /></div>
                    <div><Label className="text-[10px]">Auth Token</Label><Input type="password" value={integrationCreds.auth_token || ''} onChange={e => setIntegrationCreds({...integrationCreds, auth_token: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Cadena alfanumerica de ~32 caracteres (diferente al SID)" data-testid="integ-twilio-token" /></div>
                    <div><Label className="text-[10px]">Numero de Telefono</Label><Input value={integrationCreds.phone_number || ''} onChange={e => setIntegrationCreds({...integrationCreds, phone_number: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="+525597088217" data-testid="integ-twilio-phone" /></div>
                  </div>
                )}
                {integrationDialog === 'gupshup' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-3 space-y-2" data-testid="gupshup-guide">
                      <p className="text-[11px] font-bold text-green-400">Como obtener credenciales de Gupshup</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-green-400/70">1.</span> Ingresa a <span className="text-white">https://www.gupshup.io/developer/home</span></p>
                        <p><span className="text-green-400/70">2.</span> Ve a <span className="text-white">Dashboard {'>'} API Keys</span> para obtener tu API Key</p>
                        <p><span className="text-green-400/70">3.</span> En <span className="text-white">WhatsApp {'>'} Your Apps</span>, copia el nombre de tu app y el numero fuente</p>
                        <p><span className="text-green-400/70">4.</span> El numero fuente debe estar en formato E.164 (+52...)</p>
                      </div>
                    </div>
                    <div><Label className="text-[10px]">API Key</Label><Input value={integrationCreds.api_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, api_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Tu API Key de Gupshup" data-testid="integ-gupshup-key" /></div>
                    <div><Label className="text-[10px]">App Name</Label><Input value={integrationCreds.app_name || ''} onChange={e => setIntegrationCreds({...integrationCreds, app_name: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Nombre de tu app en Gupshup" data-testid="integ-gupshup-app" /></div>
                    <div><Label className="text-[10px]">Source Number</Label><Input value={integrationCreds.source_number || ''} onChange={e => setIntegrationCreds({...integrationCreds, source_number: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="+521234567890" /></div>
                    <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3 space-y-3" data-testid="gupshup-partner-box">
                      <p className="text-[11px] font-bold text-emerald-300">Cuenta Partner (para aprobar plantillas)</p>
                      <p className="text-[10px] text-zinc-400">
                        Esto no manda WhatsApp a nadie. Es el correo y la clave con los que Dealider le pide a WhatsApp que lea un mensaje nuevo. Pégalo una vez y listo.
                      </p>
                      <div>
                        <Label className="text-[10px] inline-flex items-center gap-1.5">
                          Correo Partner
                          <FieldTooltip tip="El email con el que entras a Gupshup Partner. Ejemplo: el de Sergio en dragontec. No es el API Key de arriba." />
                        </Label>
                        <Input
                          type="email"
                          value={integrationCreds.partner_email || ''}
                          onChange={e => setIntegrationCreds({...integrationCreds, partner_email: e.target.value})}
                          className="bg-black/20 border-white/10 text-xs mt-1"
                          placeholder="tu-correo@empresa.com"
                          data-testid="integ-gupshup-partner-email"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] inline-flex items-center gap-1.5">
                          Secret Partner
                          <FieldTooltip tip="La clave larga de Partner. Nunca se vuelve a mostrar completa. Si ya la guardaste, deja este cuadro vacío para no cambiarla." />
                        </Label>
                        <Input
                          type="password"
                          value={integrationCreds.partner_secret || ''}
                          onChange={e => setIntegrationCreds({...integrationCreds, partner_secret: e.target.value})}
                          className="bg-black/20 border-white/10 text-xs mt-1"
                          placeholder={integrationCreds.partner_secret || platformIntegrations?.gupshup?.configured ? 'Dejar vacío conserva la clave' : 'Pega aquí el secret'}
                          data-testid="integ-gupshup-partner-secret"
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                    <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 space-y-3" data-testid="gupshup-partner-box-2">
                      <p className="text-[11px] font-bold text-amber-300">2a cuenta Gupshup (SADASI / Chevrolet / TYCO)</p>
                      <p className="text-[10px] text-zinc-400">
                        Solo si tienes una SEGUNDA cuenta de Gupshup (la del wallet aparte). Pega su correo y su Client Secret y esas marcas entregaran WhatsApp sin pegar llave por app. Si no tienes 2a cuenta, deja esto vacio.
                      </p>
                      <div>
                        <Label className="text-[10px] inline-flex items-center gap-1.5">
                          Correo Partner (2a cuenta)
                          <FieldTooltip tip="El email con el que entras a la SEGUNDA cuenta de Gupshup (wallet aparte). Distinto al de arriba." />
                        </Label>
                        <Input
                          type="email"
                          value={integrationCreds.partner_email_2 || ''}
                          onChange={e => setIntegrationCreds({...integrationCreds, partner_email_2: e.target.value})}
                          className="bg-black/20 border-white/10 text-xs mt-1"
                          placeholder="correo-2a-cuenta@empresa.com"
                          data-testid="integ-gupshup-partner-email-2"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] inline-flex items-center gap-1.5">
                          Secret Partner (2a cuenta)
                          <FieldTooltip tip="El Client Secret de la SEGUNDA cuenta. Nunca se vuelve a mostrar completo. Si ya lo guardaste, deja vacio para no cambiarlo." />
                        </Label>
                        <Input
                          type="password"
                          value={integrationCreds.partner_secret_2 || ''}
                          onChange={e => setIntegrationCreds({...integrationCreds, partner_secret_2: e.target.value})}
                          className="bg-black/20 border-white/10 text-xs mt-1"
                          placeholder={integrationCreds.partner_secret_2 || platformIntegrations?.gupshup?.configured ? 'Dejar vacio conserva la clave' : 'Pega aqui el secret de la 2a cuenta'}
                          data-testid="integ-gupshup-partner-secret-2"
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {integrationDialog === 'meta_whatsapp' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 space-y-2" data-testid="meta-guide">
                      <p className="text-[11px] font-bold text-blue-400">Como obtener credenciales de Meta Cloud API</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-blue-400/70">1.</span> Ingresa a <span className="text-white">https://developers.facebook.com</span></p>
                        <p><span className="text-blue-400/70">2.</span> Ve a tu app {'>'} <span className="text-white">WhatsApp {'>'} API Setup</span></p>
                        <p><span className="text-blue-400/70">3.</span> <span className="text-white">Access Token:</span> Genera un token permanente (System User Token recomendado para produccion)</p>
                        <p><span className="text-blue-400/70">4.</span> <span className="text-white">Phone Number ID:</span> Se muestra en la seccion "From" del API Setup</p>
                        <p><span className="text-blue-400/70">5.</span> <span className="text-white">Business Account ID:</span> Ve a Business Settings {'>'} Business Info</p>
                      </div>
                      <div className="border-t border-blue-500/10 pt-2 mt-1">
                        <p className="text-[9px] text-blue-400/70">Nota: Para produccion necesitas un System User Token, no el token temporal del sandbox.</p>
                      </div>
                    </div>
                    <div><Label className="text-[10px]">Access Token</Label><Input value={integrationCreds.access_token || ''} onChange={e => setIntegrationCreds({...integrationCreds, access_token: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="EAABx..." data-testid="integ-meta-token" /></div>
                    <div><Label className="text-[10px]">Phone Number ID</Label><Input value={integrationCreds.phone_number_id || ''} onChange={e => setIntegrationCreds({...integrationCreds, phone_number_id: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Ej: 123456789012345" data-testid="integ-meta-phone-id" /></div>
                    <div><Label className="text-[10px]">Business Account ID</Label><Input value={integrationCreds.business_account_id || ''} onChange={e => setIntegrationCreds({...integrationCreds, business_account_id: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Ej: 123456789012345" /></div>
                  </div>
                )}
                {integrationDialog === 'mercado_pago' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-3 space-y-2" data-testid="mp-guide">
                      <p className="text-[11px] font-bold text-sky-400">Como obtener tu Access Token de Mercado Pago</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-sky-400/70">1.</span> Ingresa a <span className="text-white">https://www.mercadopago.com.mx/developers/panel/app</span></p>
                        <p><span className="text-sky-400/70">2.</span> Selecciona tu aplicacion (o crea una nueva)</p>
                        <p><span className="text-sky-400/70">3.</span> Ve a <span className="text-white">Credenciales de produccion</span></p>
                        <p><span className="text-sky-400/70">4.</span> Copia el <span className="text-white">Access Token</span> (APP_USR-...)</p>
                        <p><span className="text-sky-400/70">5.</span> En <span className="text-white">Webhooks</span>, configura la URL de notificaciones:</p>
                      </div>
                      <div className="mt-2 p-2 rounded bg-black/30 border border-sky-500/20">
                        <p className="text-[9px] text-sky-400/60 mb-1">URL de Webhook (copia y pega en Mercado Pago):</p>
                        <div className="flex items-center gap-1">
                          <code className="text-[10px] text-sky-300 font-mono break-all" data-testid="mp-webhook-url">{mercadopagoWebhook || '⚠️ Configura FRONTEND_URL'}</code>
                          <button type="button" onClick={() => { navigator.clipboard.writeText(mercadopagoWebhook); toast.success('URL copiada'); }} className="shrink-0 p-1 rounded hover:bg-white/5 text-sky-400/60 hover:text-sky-400">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                          </button>
                        </div>
                      </div>
                      <div className="border-t border-sky-500/10 pt-2 mt-1">
                        <p className="text-[9px] text-sky-400/70">Usa credenciales de prueba primero. Cambia a produccion cuando estes listo.</p>
                      </div>
                    </div>
                    <div><Label className="text-[10px]">Access Token</Label><Input type="password" value={integrationCreds.access_token || ''} onChange={e => setIntegrationCreds({...integrationCreds, access_token: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="APP_USR-..." data-testid="integ-mp-token" /></div>
                  </div>
                )}
                {integrationDialog === 'ai_tokens' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-3 space-y-2" data-testid="ai-guide">
                      <p className="text-[11px] font-bold text-violet-400">Donde obtener las API Keys de IA</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-violet-400/70">OpenAI:</span> <span className="text-white">https://platform.openai.com/api-keys</span> {'>'} Create new secret key</p>
                        <p><span className="text-violet-400/70">Anthropic:</span> <span className="text-white">https://console.anthropic.com/settings/keys</span> {'>'} Create Key</p>
                        <p><span className="text-violet-400/70">Google AI:</span> <span className="text-white">https://aistudio.google.com/app/apikey</span> {'>'} Get API Key</p>
                      </div>
                      <div className="border-t border-violet-500/10 pt-2 mt-1">
                        <p className="text-[9px] text-violet-400/70">Estos tokens se usan como credenciales de plataforma. Los licenciatarios con planes que incluyan "Tokens IA" no necesitaran sus propias keys.</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px]">Llave de OpenRouter</Label>
                      <Input
                        type="password"
                        value={integrationCreds.openrouter_api_key || ''}
                        onChange={(e) => setIntegrationCreds({ ...integrationCreds, openrouter_api_key: e.target.value })}
                        className="bg-black/20 border-white/10 text-xs mt-1 font-mono"
                        placeholder={platformIntegrations?.ai_tokens?.configured ? '(dejar vacio conserva la guardada)' : 'sk-or-v1-...'}
                        data-testid="integ-ai-openrouter"
                      />
                      <p className="text-[9px] text-muted-foreground mt-1">
                        Es la llave que paga el dia a dia de toda la IA. Las de abajo quedan de respaldo.
                      </p>
                    </div>
                    <div><Label className="text-[10px]">OpenAI API Key</Label><Input value={integrationCreds.openai_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, openai_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="sk-..." data-testid="integ-ai-openai" /></div>
                    <div><Label className="text-[10px]">Anthropic API Key</Label><Input value={integrationCreds.anthropic_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, anthropic_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="sk-ant-..." /></div>
                    <div><Label className="text-[10px]">Google AI Key</Label><Input value={integrationCreds.google_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, google_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="AIza..." /></div>
                  </div>
                )}
                {integrationDialog === 'stripe' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-purple-500/20 bg-purple-500/5 p-3 space-y-2" data-testid="stripe-guide">
                      <p className="text-[11px] font-bold text-purple-400">Como obtener tus credenciales de Stripe</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-purple-400/70">1.</span> Ingresa a <span className="text-white">https://dashboard.stripe.com/apikeys</span></p>
                        <p><span className="text-purple-400/70">2.</span> <span className="text-white">Secret Key:</span> Copia la clave secreta (sk_live_... para produccion, sk_test_... para pruebas)</p>
                        <p><span className="text-purple-400/70">3.</span> <span className="text-white">Webhook Secret:</span> Ve a Developers {'>'} Webhooks {'>'} Agregar endpoint {'>'} Signing secret (whsec_...)</p>
                      </div>
                      <div className="border-t border-purple-500/10 pt-2 mt-1">
                        <p className="text-[9px] text-purple-400/70">Usa claves de TEST (sk_test_) mientras configuras. Cambia a LIVE (sk_live_) cuando estes listo para cobrar.</p>
                      </div>
                    </div>
                    <div><Label className="text-[10px]">Stripe Secret Key</Label><Input type="password" value={integrationCreds.secret_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, secret_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="sk_test_... o sk_live_..." data-testid="integ-stripe-key" /></div>
                    <div><Label className="text-[10px]">Webhook Secret</Label><Input type="password" value={integrationCreds.webhook_secret || ''} onChange={e => setIntegrationCreds({...integrationCreds, webhook_secret: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="whsec_..." /></div>
                  </div>
                )}
                {integrationDialog === 'mercado_pago' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-3 space-y-2" data-testid="mp-guide">
                      <p className="text-[11px] font-bold text-sky-400">Como obtener tu Access Token de Mercado Pago</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-sky-400/70">1.</span> Ingresa a <span className="text-white">https://www.mercadopago.com.mx/developers/panel/app</span></p>
                        <p><span className="text-sky-400/70">2.</span> Selecciona tu aplicacion (o crea una nueva)</p>
                        <p><span className="text-sky-400/70">3.</span> Ve a <span className="text-white">Credenciales de produccion</span></p>
                        <p><span className="text-sky-400/70">4.</span> Copia el <span className="text-white">Access Token</span> (APP_USR-...)</p>
                      </div>
                      <div className="border-t border-sky-500/10 pt-2 mt-1">
                        <p className="text-[9px] text-sky-400/70">Usa credenciales de prueba primero. Cambia a produccion cuando estes listo.</p>
                      </div>
                    </div>
                    <div><Label className="text-[10px]">Access Token</Label><Input type="password" value={integrationCreds.access_token || ''} onChange={e => setIntegrationCreds({...integrationCreds, access_token: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="APP_USR-..." data-testid="integ-mp-token" /></div>
                  </div>
                )}

                {integrationDialog === 'r2' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3 space-y-2" data-testid="r2-guide">
                      <p className="text-[11px] font-bold text-orange-400">Como obtener tus credenciales de Cloudflare R2</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-orange-400/70">1.</span> Ingresa a <span className="text-white">dash.cloudflare.com {'>'} R2 Object Storage</span></p>
                        <p><span className="text-orange-400/70">2.</span> <span className="text-white">Manage R2 API Tokens {'>'} Create API Token</span>, permisos Object Read &amp; Write, acotado a UN bucket</p>
                        <p><span className="text-orange-400/70">3.</span> Copia <span className="text-white">Access Key ID</span> y <span className="text-white">Secret Access Key</span> (esta última solo se muestra UNA vez en Cloudflare)</p>
                        <p><span className="text-orange-400/70">4.</span> <span className="text-white">Account ID</span> (32 caracteres hex) está arriba a la derecha del panel de R2</p>
                      </div>
                      <div className="border-t border-orange-500/10 pt-2 mt-1">
                        <p className="text-[9px] text-orange-400/70">Todos los campos empiezan vacíos al editar — dejar uno vacío CONSERVA el valor ya guardado, no lo borra. Solo se sobreescribe lo que escribas.</p>
                      </div>
                    </div>
                    {platformIntegrations?.r2?.configured && (
                      <div className="flex items-center gap-2 text-[10px] text-emerald-400 bg-emerald-500/5 border border-emerald-500/20 rounded px-2 py-1.5" data-testid="r2-configured-badge">
                        <CheckCircle2 className="w-3 h-3" /> Configurado — los campos abajo muestran el valor actual enmascarado como referencia (placeholder), no editable directo.
                      </div>
                    )}
                    <div><Label className="text-[10px]">Account ID</Label><Input value={integrationCreds.account_id || ''} onChange={e => setIntegrationCreds({...integrationCreds, account_id: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={platformIntegrations?.r2?.credentials?.account_id || '32 caracteres hex'} data-testid="integ-r2-account-id" /></div>
                    <div><Label className="text-[10px]">Access Key ID</Label><Input value={integrationCreds.access_key_id || ''} onChange={e => setIntegrationCreds({...integrationCreds, access_key_id: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={platformIntegrations?.r2?.credentials?.access_key_id || 'Access Key ID'} data-testid="integ-r2-access-key" /></div>
                    <div><Label className="text-[10px]">Secret Access Key</Label><Input type="password" value={integrationCreds.secret_access_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, secret_access_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={platformIntegrations?.r2?.configured ? '(dejar vacío conserva el valor guardado)' : 'Secret Access Key'} data-testid="integ-r2-secret" /></div>
                    <div><Label className="text-[10px]">Bucket</Label><Input value={integrationCreds.bucket || ''} onChange={e => setIntegrationCreds({...integrationCreds, bucket: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={platformIntegrations?.r2?.credentials?.bucket || 'ej: dealider-prod'} data-testid="integ-r2-bucket" /></div>
                    <div><Label className="text-[10px]">Endpoint</Label><Input value={integrationCreds.endpoint || ''} onChange={e => setIntegrationCreds({...integrationCreds, endpoint: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={platformIntegrations?.r2?.credentials?.endpoint || 'https://<account_id>.r2.cloudflarestorage.com'} data-testid="integ-r2-endpoint" /></div>
                  </div>
                )}

                {integrationDialog === 'sendgrid' && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3 space-y-2" data-testid="sendgrid-guide">
                      <p className="text-[11px] font-bold text-cyan-400">Como obtener tu API Key de SendGrid</p>
                      <div className="space-y-1 text-[10px] text-muted-foreground">
                        <p><span className="text-cyan-400/70">1.</span> Ingresa a <span className="text-white">https://app.sendgrid.com/settings/api_keys</span></p>
                        <p><span className="text-cyan-400/70">2.</span> Crea una API Key con permisos <span className="text-white">Mail Send: Full Access</span></p>
                        <p><span className="text-cyan-400/70">3.</span> Copia la clave que inicia con <span className="text-white">SG.</span> (solo se muestra una vez)</p>
                        <p><span className="text-cyan-400/70">4.</span> Valida tu email remitente en <span className="text-white">Settings &gt; Sender Authentication</span> antes de enviar</p>
                      </div>
                      <div className="border-t border-cyan-500/10 pt-2 mt-1">
                        <p className="text-[9px] text-cyan-400/70">La API Key se validara contra SendGrid antes de guardarse.</p>
                      </div>
                    </div>

                    {sendGridConfig.configured && (
                      <div className="flex items-center gap-2 text-[10px] text-emerald-400 bg-emerald-500/5 border border-emerald-500/20 rounded px-2 py-1.5" data-testid="sendgrid-configured-badge">
                        <CheckCircle2 className="w-3 h-3" /> Configurado. Clave actual: <span className="font-mono">{sendGridConfig.masked_key}</span>
                      </div>
                    )}

                    <div>
                      <Label className="text-[10px]">API Key (SG...)</Label>
                      <Input type="password" value={sendGridConfig.sendgrid_api_key} onChange={e => setSendGridConfig(c => ({ ...c, sendgrid_api_key: e.target.value }))} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={sendGridConfig.configured ? 'Actualizar clave (dejar vacio mantiene actual)' : 'SG.xxxxxxxx...'} data-testid="sendgrid-api-key" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-[10px]">Email Remitente</Label><Input type="email" value={sendGridConfig.sendgrid_from_email} onChange={e => setSendGridConfig(c => ({ ...c, sendgrid_from_email: e.target.value }))} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="no-reply@dealider.com" data-testid="sendgrid-from-email" /></div>
                      <div><Label className="text-[10px]">Nombre Remitente</Label><Input value={sendGridConfig.sendgrid_from_name} onChange={e => setSendGridConfig(c => ({ ...c, sendgrid_from_name: e.target.value }))} className="bg-black/20 border-white/10 text-xs mt-1" placeholder={APP_NAME} data-testid="sendgrid-from-name" /></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="sg-active" checked={sendGridConfig.is_active} onChange={e => setSendGridConfig(c => ({ ...c, is_active: e.target.checked }))} data-testid="sendgrid-active-toggle" />
                      <label htmlFor="sg-active" className="text-[10px] text-muted-foreground">Activo (disponible para tenants sin configuracion propia)</label>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <Button onClick={saveSendGridConfig} disabled={sendGridBusy} className="bg-cyan-600 hover:bg-cyan-500 text-xs" data-testid="sendgrid-save-btn">
                        {sendGridBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Validar y Guardar
                      </Button>
                      <div className="flex-1" />
                      <Input type="email" value={sendGridTestEmail} onChange={e => setSendGridTestEmail(e.target.value)} placeholder="destino@prueba.com" className="bg-black/20 border-white/10 text-xs w-44" data-testid="sendgrid-test-email" />
                      <Button variant="outline" onClick={testSendGridConfig} disabled={sendGridBusy || !sendGridConfig.configured} className="text-xs border-white/10" data-testid="sendgrid-test-btn">
                        {sendGridBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Probar envio
                      </Button>
                    </div>
                  </div>
                )}

                {integrationDialog === 'voice_live' && (
                  <div className="space-y-3" data-testid="voice-live-fields">
                    <div className="rounded-lg border border-pink-500/20 bg-pink-500/5 p-3 space-y-1 text-[10px] text-muted-foreground">
                      <p className="text-[11px] font-bold text-pink-400">Voz en vivo (motor B - voz natural)</p>
                      <p>Grok Voice (xAI) es el mas barato (~US$0.05/min). OpenAI Realtime es respaldo mas caro.</p>
                      <p className="text-pink-400/70">Donde: console.x.ai &rarr; API keys &middot; platform.openai.com &rarr; API keys</p>
                      <p>Sin llave, los tenants en motor B caen al motor A (Polly). Nadie se queda sin contestacion.</p>
                    </div>
                    <div>
                      <Label className="text-[10px]">Proveedor</Label>
                      <select value={integrationCreds.proveedor || 'auto'} onChange={e => setIntegrationCreds({...integrationCreds, proveedor: e.target.value})} className="w-full bg-black/20 border border-white/10 rounded text-xs mt-1 p-2" data-testid="integ-voicelive-proveedor">
                        <option value="auto">Automatico (Grok si hay, si no OpenAI)</option>
                        <option value="grok">Grok Voice (xAI)</option>
                        <option value="openai">OpenAI Realtime</option>
                      </select>
                    </div>
                    <div><Label className="text-[10px]">Grok API Key (xAI)</Label><Input type="password" value={integrationCreds.grok_api_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, grok_api_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={platformIntegrations?.voice_live?.configured ? '(dejar vacio conserva el valor)' : 'xai-...'} data-testid="integ-voicelive-grok" /></div>
                    <div><Label className="text-[10px]">OpenAI Realtime API Key</Label><Input type="password" value={integrationCreds.openai_api_key || ''} onChange={e => setIntegrationCreds({...integrationCreds, openai_api_key: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1 font-mono" placeholder={platformIntegrations?.voice_live?.configured ? '(dejar vacio conserva el valor)' : 'sk-...'} data-testid="integ-voicelive-openai" /></div>
                  </div>
                )}

                {integrationDialog !== 'sendgrid' && (
                  <Button onClick={() => savePlatformCredentials(integrationDialog)} className="w-full bg-pink-600 hover:bg-pink-500 text-xs" data-testid="save-integ-btn">
                    <Save className="w-3.5 h-3.5 mr-1" /> Guardar Credenciales
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* TAB: Audit Log (Bitacora) */}
        <TabsContent value="audit">
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="audit-log-panel">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2"><FileText className="w-5 h-5 text-cyan-400" /> Bitacora de Actividad</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Registro de todas las acciones significativas en la plataforma ({auditTotal} registros)</p>
                </div>
              </div>

              {/* Company filter */}
              <div className="flex gap-2 mb-4">
                <Select value={auditCompanyId || '_all'} onValueChange={v => { const cid = v === '_all' ? '' : v; setAuditCompanyId(cid); loadAuditLogs(cid); }}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs w-64" data-testid="audit-company-filter"><SelectValue placeholder="Filtrar por empresa" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todas las empresas</SelectItem>
                    {(stats?.companies || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => loadAuditLogs(auditCompanyId)} className="border-white/10 text-xs h-9" data-testid="audit-refresh-btn">
                  <Search className="w-3 h-3 mr-1" /> Actualizar
                </Button>
              </div>

              {auditLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No hay registros en la bitacora</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                  {auditLogs.map(log => (
                    <div key={log.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-black/20 border border-white/5 text-xs" data-testid={`audit-${log.id}`}>
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                        log.action === 'login' ? 'bg-emerald-500/10' :
                        log.action === 'login_failed' ? 'bg-red-500/10' :
                        log.action?.includes('created') ? 'bg-blue-500/10' :
                        log.action?.includes('deleted') ? 'bg-red-500/10' :
                        'bg-cyan-500/10'
                      }`}>
                        <Clock className={`w-3.5 h-3.5 ${
                          log.action === 'login' ? 'text-emerald-400' :
                          log.action === 'login_failed' ? 'text-red-400' :
                          log.action?.includes('created') ? 'text-blue-400' :
                          log.action?.includes('deleted') ? 'text-red-400' :
                          'text-cyan-400'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium">{log.user_name}</span>
                          <Badge className="text-[8px] bg-white/5">{log.action}</Badge>
                          {log.entity_type && <Badge className="text-[8px] bg-cyan-500/10 text-cyan-400">{log.entity_type}</Badge>}
                        </div>
                        {log.details && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{log.details}</p>}
                      </div>
                      <span className="text-[9px] text-muted-foreground whitespace-nowrap">{new Date(log.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md bg-[#0a0c12] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#A5D62C]" /> Nueva Cuenta
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">Registra una nueva cuenta en la plataforma {APP_NAME}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Paso 1 — Industria</Label>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                {INDUSTRY_OPTIONS.map(ind => (
                  <button key={ind.value} type="button" onClick={() => setForm({...form, industry: ind.value, industry_subtype: '', business_type: 'agency'})}
                    className={`p-2.5 rounded-lg border text-left transition-all ${form.industry === ind.value ? `border-${ind.color}-400/50 bg-${ind.color}-500/5` : 'border-white/[0.06] bg-white/[0.02] hover:border-white/10'}`}
                    data-testid={`admin-industry-${ind.value}`}>
                    <p className={`text-sm ${form.industry === ind.value ? 'text-white' : 'text-white/40'}`}>{ind.icon}</p>
                    <p className={`text-[10px] font-bold ${form.industry === ind.value ? 'text-white' : 'text-white/40'}`}>{ind.label}</p>
                    <p className="text-[8px] text-white/20 mt-0.5">{ind.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            {form.industry && INDUSTRY_SUBTYPES[form.industry] && (
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">Paso 2 — Tipo de Negocio</Label>
                <div className="grid grid-cols-2 gap-2">
                  {INDUSTRY_SUBTYPES[form.industry].map(sub => (
                    <button key={sub.value} type="button" onClick={() => setForm({...form, industry_subtype: sub.value, business_type: sub.btype})}
                      className={`p-2.5 rounded-lg border text-left transition-all ${form.industry_subtype === sub.value ? 'border-teal-400/50 bg-teal-500/5' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/10'}`}
                      data-testid={`admin-subtype-${sub.value}`}>
                      <p className={`text-[10px] font-bold ${form.industry_subtype === sub.value ? 'text-white' : 'text-white/40'}`}>{sub.label}</p>
                      <p className="text-[8px] text-white/20 mt-0.5">{sub.desc}</p>
                      <p className="text-[7px] text-teal-400/50 mt-1">Modulos: {sub.modules}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Plan</Label>
              <Select value={form.plan} onValueChange={v => setForm({...form, plan: v})}>
                <SelectTrigger className="bg-white/[0.04] border-white/[0.06]" data-testid="admin-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableDynamicPlans.map(p => (
                    <SelectItem key={p.id} value={p.id} data-testid={`admin-plan-option-${p.id}`}>
                      {p.name} {Number(p.price || 0) > 0 ? `($${Number(p.price || 0).toLocaleString()}/${p.billing_period === 'year' ? 'año' : p.billing_period === 'quarter' ? 'trimestre' : p.billing_period === 'semiannual' ? 'semestre' : 'mes'})` : '(Gratis)'}
                    </SelectItem>
                  ))}
                  {!availableDynamicPlans.length && (
                    <SelectItem value="__no_plans__" disabled data-testid="admin-plan-option-empty">
                      Sin planes dinámicos activos para este tipo
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground" data-testid="admin-plan-source-note">Fuente: solo planes dinámicos activos (`dynamic_plans`).</p>
              {isCatalogIndustry(form.industry) && (
                <p className="text-[10px] text-teal-400/80" data-testid="admin-plan-catalog-note">
                  Este vertical incluye el módulo Productos y Servicios (fotos, PDF, video, YouTube). Elige Educacion IA o Generico IA; si el plan no lo trae, el alta lo enciende igual.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">{form.industry === 'education' ? 'Nombre del Colegio / Universidad' : form.industry === 'automotive' ? 'Nombre de la Agencia de Autos' : form.business_type === 'independent' ? 'Nombre del Promotor' : form.business_type === 'developer' ? 'Nombre del Desarrollador' : form.business_type === 'franchise' ? 'Nombre del Franquiciatario' : form.business_type === 'owner' ? 'Nombre del Propietario' : 'Nombre de la Empresa'}</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder={form.industry === 'education' ? 'Colegio Atlas' : form.industry === 'automotive' ? 'Agencia Autos Centro' : 'Empresa'} className="bg-white/[0.04] border-white/[0.06]" data-testid="admin-company-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Nombre Administrador</Label>
              <Input value={form.admin_name} onChange={e => setForm({...form, admin_name: e.target.value})} required placeholder="Nombre del administrador" className="bg-white/[0.04] border-white/[0.06]" data-testid="admin-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">Email</Label>
                <Input type="email" value={form.admin_email} onChange={e => setForm({...form, admin_email: e.target.value})} required placeholder="admin@empresa.com" className="bg-white/[0.04] border-white/[0.06]" data-testid="admin-email" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">Telefono</Label>
                <PhoneInput value={form.phone} onChange={v => setForm({...form, phone: v})} data-testid="admin-phone" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Contrasena</Label>
              <Input type="password" value={form.admin_password} onChange={e => setForm({...form, admin_password: e.target.value})} required placeholder="Min 6 caracteres" className="bg-white/[0.04] border-white/[0.06]" data-testid="admin-password" />
            </div>
            {(form.business_type === 'developer' || form.business_type === 'franchise' || form.business_type === 'car_dealer') && (
              <div className="p-3 rounded-lg bg-violet-500/[0.05] border border-violet-500/10">
                <p className="text-xs text-violet-400/80">Las cuentas de Desarrollador, Franquiciatario y Car-Dealer tienen acceso avanzado y se crean desde Super Admin.</p>
              </div>
            )}
            <Button type="submit" disabled={saving} className="w-full bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold" data-testid="submit-create-company">
              {saving ? 'Creando...' : 'Crear Cuenta'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Límite de almacenamiento (Biblioteca) — edición manual, sin flujo de compra/pago */}
      <Dialog open={!!storageEditCompany} onOpenChange={(open) => !open && setStorageEditCompany(null)}>
        <DialogContent className="max-w-sm bg-[#0a0c12] border-white/[0.06]" data-testid="storage-limit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-orange-400" /> Límite de almacenamiento
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Biblioteca de <strong>{storageEditCompany?.name}</strong>. Default de un tenant nuevo: 1024MB (1GB).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
              <div>
                <p className="text-xs font-semibold text-white">Biblioteca (R2) habilitada</p>
                <p className="text-[10px] text-muted-foreground">Rollout gradual — apagado no borra archivos existentes</p>
              </div>
              <Switch
                checked={!!storageEditCompany?.biblioteca_r2_enabled}
                onCheckedChange={toggleBibliotecaR2}
                disabled={biblioR2Saving}
                data-testid="biblioteca-r2-toggle"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Límite en MB</Label>
              <Input
                type="number"
                min={1}
                value={storageEditMb}
                onChange={e => setStorageEditMb(e.target.value)}
                className="bg-white/[0.04] border-white/[0.06]"
                data-testid="storage-limit-input"
              />
            </div>
            <Button
              onClick={saveStorageLimit}
              disabled={storageSaving}
              className="w-full bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold"
              data-testid="storage-limit-save"
            >
              {storageSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!vozTenant} onOpenChange={(open) => !open && setVozTenant(null)}>
        <DialogContent className="max-w-sm bg-[#0a0c12] border-white/[0.06]" data-testid="voz-tenant-creds-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2"><Phone className="w-5 h-5 text-emerald-400" /> Voz IA con Twilio propio</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">Credenciales Twilio PROPIAS de <strong>{vozTenant?.name}</strong> para su IA de voz (llamadas que SALEN). El tenant las podra ver/editar en su Configuracion.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-white/60 text-xs">Numero (desde el que llama la IA)</Label><Input value={vozForm.phone_number} onChange={e => setVozForm({ ...vozForm, phone_number: e.target.value })} placeholder="+525512345678" className="bg-white/[0.04] border-white/[0.06]" data-testid="voz-tenant-phone" /></div>
            <div><Label className="text-white/60 text-xs">Account SID</Label><Input value={vozForm.account_sid} onChange={e => setVozForm({ ...vozForm, account_sid: e.target.value })} placeholder="AC..." className="bg-white/[0.04] border-white/[0.06]" data-testid="voz-tenant-sid" /></div>
            <div><Label className="text-white/60 text-xs">Auth Token</Label><Input type="password" value={vozForm.auth_token} onChange={e => setVozForm({ ...vozForm, auth_token: e.target.value })} placeholder="Token secreto" className="bg-white/[0.04] border-white/[0.06]" data-testid="voz-tenant-token" /></div>
            <Button onClick={saveVozTenant} disabled={vozTenantSaving} className="w-full bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold" data-testid="voz-tenant-save">{vozTenantSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

